(function () {
  // ---------------------------------------------------------------------------
  // State
  // ---------------------------------------------------------------------------
  var state = {
    config: null,
    cart: null,
    cartRequest: null,
    activeTab: 'all',
    initialized: false,
    refreshTimer: null,
    applyingCode: null,
    removingCode: false,
    savingsPopupTimer: null,
    savingsPopupAutoCloseTimer: null,
    savingsPopupFrame: null,
    lastCartAction: ''
  };

  var POPUP_DURATION = 3000;
  var autoApplyChecked = false; // Only check for auto-applied discounts once per page load

  // ---------------------------------------------------------------------------
  // Cloudflare Worker backend (source of truth for coupon state)
  // ---------------------------------------------------------------------------
  var WORKER_URL = 'https://yellow-band-d56c.wearvayron.workers.dev';

  function getCartToken() {
    // Shopify stores the cart token in a cookie named "cart"
    var match = document.cookie.match(/(?:^|;\s*)cart=([^;]*)/);
    if (match) return decodeURIComponent(match[1]);
    // Fallback: try cart_token from cart object
    if (state.cart && state.cart.token) return state.cart.token;
    // Last resort: generate a session-unique ID
    var key = 'vayronCartTokenFallback';
    var stored = '';
    try { stored = window.sessionStorage.getItem(key); } catch (e) {}
    if (stored) return stored;
    stored = 'fb_' + Date.now() + '_' + Math.random().toString(36).slice(2, 10);
    try { window.sessionStorage.setItem(key, stored); } catch (e) {}
    return stored;
  }

  function workerApply(code) {
    return fetch(WORKER_URL + '/apply', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ code: code, cartToken: getCartToken() })
    }).then(function (r) { return r.json(); }).catch(function () { return null; });
  }

  function workerRemove(code) {
    return fetch(WORKER_URL + '/remove', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ code: code || '', cartToken: getCartToken() })
    }).then(function (r) { return r.json(); }).catch(function () { return null; });
  }

  function workerGetState() {
    return fetch(WORKER_URL + '/state?cart=' + encodeURIComponent(getCartToken()))
      .then(function (r) { return r.json(); })
      .catch(function () { return { codes: [] }; });
  }

  function workerGetCoupons() {
    var configured = (state.config && state.config.coupons) || [];
    var codes = configured
      .map(function (c) { return (c && c.code) ? String(c.code).trim().toUpperCase() : ''; })
      .filter(Boolean);
    if (codes.length === 0) return Promise.resolve({ rules: {} });
    var qs = encodeURIComponent(codes.join(','));
    var url = WORKER_URL + '/coupons?codes=' + qs;

    function doFetch() {
      return fetch(url).then(function (r) { return r.json(); });
    }

    // Try fetch, retry once after 2s on failure, then try XHR as last resort
    return doFetch().catch(function () {
      return wait(2000).then(function () {
        return doFetch().catch(function () {
          // XHR fallback — some browsers block fetch but allow XHR
          return new Promise(function (resolve) {
            try {
              var xhr = new XMLHttpRequest();
              xhr.open('GET', url, true);
              xhr.onload = function () {
                try { resolve(JSON.parse(xhr.responseText)); }
                catch (e) { resolve({ rules: {} }); }
              };
              xhr.onerror = function () { resolve({ rules: {} }); };
              xhr.ontimeout = function () { resolve({ rules: {} }); };
              xhr.timeout = 8000;
              xhr.send();
            } catch (e) {
              resolve({ rules: {} });
            }
          });
        });
      });
    });
  }

  // Coupon rules from Shopify (cached by Worker)
  // Map of CODE → { kind, entitled: { allItems, productIds[] }, customerBuys, discountType, discountValue, rewardQuantity }
  var couponRules = {};

  function getCouponRule(code) {
    if (!code) return null;
    return couponRules[String(code).trim().toUpperCase()] || null;
  }

  // Check if a cart item is eligible based on a rule's entitled list
  function isItemEligible(item, entitled) {
    if (!entitled) return true;
    if (entitled.allItems) return true;
    if (!entitled.productIds) return true;
    // Empty productIds means scoped but nothing resolved — deny rather than allow all
    if (entitled.productIds.length === 0) return false;
    var pid = String(item.product_id || item.id || '');
    return entitled.productIds.indexOf(pid) !== -1;
  }

  // Check whether the customerBuys requirement is met for a BXGY rule
  function meetsCustomerBuysRequirement(rule, cart) {
    if (!rule || rule.kind !== 'bxgy' || !rule.customerBuys) return true;
    if (rule.customerBuys.allItems) return true;
    if (!cart || !Array.isArray(cart.items)) return false;

    // Collect eligible items and totals in one pass
    var eligibleTotal = 0;
    var eligibleQty = 0;
    cart.items.forEach(function (item) {
      if (isItemEligible(item, rule.customerBuys)) {
        eligibleTotal += Number(item.original_line_price || item.line_price || item.price || 0);
        eligibleQty += (item.quantity || 0);
      }
    });

    // Amount-based requirement (e.g. ₹1999 of eligible items)
    if (rule.buysAmount && rule.buysAmount > 0) {
      // buysAmount is in rupees, cart prices are in paise (cents)
      if (eligibleTotal < rule.buysAmount * 100) return false;
      // Amount check passed — no further quantity check needed for amount-based rules
      return true;
    }

    // Quantity-based requirement (e.g. buy 2 eligible items)
    if (rule.buysQuantity && rule.buysQuantity > 0) {
      return eligibleQty >= rule.buysQuantity;
    }

    // No specific requirement — just need at least 1 eligible item
    return eligibleQty >= 1;
  }

  // ---------------------------------------------------------------------------
  // SVG icons
  // ---------------------------------------------------------------------------
  function getCardIconMarkup() {
    if (state.config && state.config.cardIconUrl) {
      var size = state.config.cardIconSize || 20;
      return '<img src="' + escapeHtml(state.config.cardIconUrl) + '" width="' + size + '" height="' + size + '" alt="" loading="lazy">';
    }
    return '';
  }

  var arrowIcon = [
    '<svg viewBox="0 0 20 20" aria-hidden="true" focusable="false">',
    '<path d="M7.72 4.22a.75.75 0 0 1 1.06 0l5.25 5.25a.75.75 0 0 1 0 1.06l-5.25 5.25a.75.75 0 1 1-1.06-1.06L12.44 10 7.72 5.28a.75.75 0 0 1 0-1.06Z" fill="currentColor"></path>',
    '</svg>'
  ].join('');

  var chevronDownIcon = [
    '<svg viewBox="0 0 20 20" aria-hidden="true" focusable="false">',
    '<path d="M5.23 7.21a.75.75 0 0 1 1.06.02L10 11.12l3.71-3.89a.75.75 0 1 1 1.08 1.04l-4.25 4.45a.75.75 0 0 1-1.08 0L5.21 8.27a.75.75 0 0 1 .02-1.06Z" fill="currentColor"></path>',
    '</svg>'
  ].join('');

  // ---------------------------------------------------------------------------
  // Utilities
  // ---------------------------------------------------------------------------
  function escapeHtml(value) {
    return String(value == null ? '' : value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function toNumber(value) {
    var n = parseFloat(value);
    return Number.isFinite(n) ? n : 0;
  }

  function wait(ms) {
    return new Promise(function (resolve) {
      setTimeout(resolve, ms);
    });
  }

  // ---------------------------------------------------------------------------
  // Money formatting
  // ---------------------------------------------------------------------------
  function getLocale(currency) {
    if (currency === 'INR') return 'en-IN';
    return (state.config && state.config.locale) || document.documentElement.lang || 'en';
  }

  function getCurrency(cart) {
    return (cart && cart.currency) || (state.config && state.config.shopCurrency) || 'INR';
  }

  function formatMoney(cents, currency) {
    var amount = Math.round(cents) / 100;
    try {
      return new Intl.NumberFormat(getLocale(currency), {
        style: 'currency',
        currency: currency || 'INR',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format(amount);
    } catch (e) {
      return '\u20B9' + Math.round(amount);
    }
  }

  function formatSavingsAmount(cents, currency) {
    var amount = Math.round(cents) / 100;
    try {
      var formatted = new Intl.NumberFormat(getLocale(currency), {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format(amount);
      return (currency === 'INR' ? '\u20B9' : '') + formatted;
    } catch (e) {
      return '\u20B9' + Math.round(amount);
    }
  }

  function formatDiscountValue(value) {
    return String(Math.round(toNumber(value)));
  }

  function formatCount(value) {
    return String(Math.round(toNumber(value)));
  }

  // ---------------------------------------------------------------------------
  // DOM helpers
  // ---------------------------------------------------------------------------
  function getRoot() {
    return document.querySelector('[data-cart-coupons-root]');
  }

  function getSheet() {
    return document.querySelector('[data-cart-coupon-sheet]');
  }

  function getPanel() {
    return document.querySelector('[data-cart-coupon-panel]');
  }

  function getList() {
    return document.querySelector('[data-cart-coupon-list]');
  }

  function getSuccessPopup() {
    return document.querySelector('[data-cart-coupon-success]');
  }

  function getTabButtons() {
    return Array.prototype.slice.call(document.querySelectorAll('[data-cart-coupon-tab]'));
  }

  // ---------------------------------------------------------------------------
  // Mini-cart integration
  // ---------------------------------------------------------------------------
  function getMiniCartModule() {
    return window.theme && window.theme.miniCart ? window.theme.miniCart : null;
  }

  function isMiniCartOpen() {
    return !!(
      document.querySelector('.cardraw.active') ||
      document.querySelector('.js-mini-cart.active')
    );
  }

  function beginMiniCartLoading() {
    var mod = getMiniCartModule();
    if (mod && typeof mod.beginLoading === 'function') {
      mod.beginLoading();
    }
  }

  function endMiniCartLoading() {
    var mod = getMiniCartModule();
    if (mod && typeof mod.endLoading === 'function') {
      mod.endLoading();
    }
  }

  function syncMiniCart(cart) {
    var mod = getMiniCartModule();
    if (mod && typeof mod.generateCart === 'function') {
      return Promise.resolve(mod.generateCart(cart));
    }
    return Promise.resolve();
  }

  function requestMiniCartCart(preferCurrent) {
    var mod = getMiniCartModule();
    if (!mod) return null;

    if (preferCurrent && typeof mod.getCurrentCart === 'function') {
      var current = mod.getCurrentCart();
      if (current && typeof current.item_count !== 'undefined') {
        return Promise.resolve(current);
      }
    }

    if (typeof mod.loadCart === 'function') {
      return Promise.resolve(mod.loadCart());
    }

    return null;
  }

  // ---------------------------------------------------------------------------
  // Worker-backed user-applied codes (local cache synced with Worker)
  // ---------------------------------------------------------------------------
  var workerAppliedCodes = []; // Local cache of codes the Worker knows about

  function getUserAppliedCodes() {
    return workerAppliedCodes;
  }

  function addUserAppliedCode(code) {
    var normalized = String(code).trim().toUpperCase();
    if (!normalized) return;
    if (workerAppliedCodes.indexOf(normalized) === -1) {
      workerAppliedCodes.push(normalized);
    }
  }

  function removeUserAppliedCode(code) {
    var normalized = String(code).trim().toUpperCase();
    workerAppliedCodes = workerAppliedCodes.filter(function (c) { return c !== normalized; });
  }

  function isUserAppliedCode(code) {
    return workerAppliedCodes.indexOf(String(code).trim().toUpperCase()) !== -1;
  }

  // Check if a discount code matches one of our configured coupons
  function isKnownCouponCode(code) {
    if (!state.config || !state.config.coupons) return false;
    var normalized = String(code).trim().toUpperCase();
    return state.config.coupons.some(function (c) { return c.code === normalized; });
  }

  // On page load: clear STALE Shopify discount sessions.
  // If the user has an active coupon in Worker state, Shopify's discount is kept.
  // Only runs once per page load.
  function clearAutoAppliedDiscounts(cart) {
    if (!cart || autoApplyChecked) return;
    autoApplyChecked = true;

    // If user has an active coupon (from ANY source), don't clear Shopify's discount
    var activeCode = optimisticAppliedCode || (workerAppliedCodes.length > 0 ? workerAppliedCodes[0] : '');
    if (!activeCode) {
      // Also check sessionStorage — survives Worker failures
      var sessionCodes = getSessionAppliedCodes();
      for (var i = 0; i < sessionCodes.length; i++) {
        var sc = String(sessionCodes[i]).trim().toUpperCase();
        if (sc && !isCodeSuppressed(sc)) { activeCode = sc; break; }
      }
    }
    if (activeCode) return;

    // Check if Shopify has any code-based discount
    var hasCodeDiscount = false;
    if (Array.isArray(cart.cart_level_discount_applications)) {
      hasCodeDiscount = cart.cart_level_discount_applications.some(function (app) {
        return app && app.type === 'discount_code';
      });
    }
    if (!hasCodeDiscount) return;

    // Stale discount — clear cookie and server session
    clearDiscountCookieFallback();
    try {
      var iframe = document.createElement('iframe');
      iframe.style.display = 'none';
      var cleared = false;

      var onCleared = function () {
        if (cleared) return;
        cleared = true;
        try { iframe.remove(); } catch (e) {}
        requestCart().then(function (freshCart) {
          if (freshCart) {
            stripCodeDiscounts(freshCart);
            state.cart = freshCart;
            render();
            syncCartWithCoupon();
          }
        });
      };

      iframe.onload = function () { setTimeout(onCleared, 100); };
      iframe.onerror = onCleared;
      iframe.src = '/discount/%20?redirect=/cart';
      document.body.appendChild(iframe);
      setTimeout(onCleared, 2000);
    } catch (e) {}
  }

  // ---------------------------------------------------------------------------
  // Cookie helpers
  // ---------------------------------------------------------------------------
  function clearDiscountCookieFallback() {
    var paths = ['/', '/cart', '/checkout', '/collections', '/products'];
    var domains = [window.location.hostname, '.' + window.location.hostname, ''];
    paths.forEach(function (path) {
      domains.forEach(function (domain) {
        var base = 'discount_code=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=' + path;
        document.cookie = domain ? (base + '; domain=' + domain) : base;
        // Also try max-age=0
        var base2 = 'discount_code=; max-age=0; path=' + path;
        document.cookie = domain ? (base2 + '; domain=' + domain) : base2;
      });
    });
    // Overwrite with empty value as well
    document.cookie = 'discount_code=; path=/; max-age=0';
    document.cookie = 'discount_code=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT';
  }

  // ---------------------------------------------------------------------------
  // (Shopify discount routes removed — discounts now applied only at checkout)
  // ---------------------------------------------------------------------------

  // ---------------------------------------------------------------------------
  // Config parser
  // ---------------------------------------------------------------------------
  function parseConfig() {
    var root = getRoot();
    if (!root) return null;

    var script = root.querySelector('[data-cart-coupons-config]');
    if (!script) return null;

    try {
      var raw = JSON.parse(script.textContent);
    } catch (e) {
      return null;
    }

    if (!raw || !raw.enabled) return null;
    if (!Array.isArray(raw.coupons) || raw.coupons.length === 0) return null;

    raw.coupons = raw.coupons
      .map(function (c) {
        return {
          id: c.id || '',
          code: String(c.code || '').trim().toUpperCase(),
          offerTitle: String(c.offerTitle || '').trim(),
          offerDescription: String(c.offerDescription || '').trim(),
          minimumCartValue: toNumber(c.minimumCartValue),
          discountType: c.discountType === 'fixed' ? 'fixed' : 'percentage',
          discountValue: toNumber(c.discountValue),
          discountKind: c.discountKind === 'bxgy' ? 'bxgy' : 'basic',
          scopeType: String(c.scopeType || 'AllDiscountItems'),
          requirementType: (c.requirementType === 'quantity' || c.requirementType === 'subtotal') ? c.requirementType : 'none',
          requirementValue: toNumber(c.requirementValue),
          rewardQuantity: toNumber(c.rewardQuantity),
          tag: c.tag === 'brand' ? 'brand' : 'all',
          displaySurface: c.displaySurface === 'popup' ? 'popup' : 'both',
          cardDescription: String(c.cardDescription || '').trim(),
          cardOffer: String(c.cardOffer || '').trim()
        };
      })
      .filter(function (c) {
        return c.code && c.discountValue > 0;
      });

    if (raw.coupons.length === 0) return null;

    return raw;
  }

  // ---------------------------------------------------------------------------
  // Cart data helpers
  // ---------------------------------------------------------------------------
  function requestCart(force) {
    var miniCartReq = requestMiniCartCart(!force);
    if (miniCartReq) return miniCartReq;

    if (!force && state.cartRequest) return state.cartRequest;

    state.cartRequest = fetch('/cart.js', {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' }
    })
      .then(function (r) { return r.json(); })
      .finally(function () { state.cartRequest = null; });

    return state.cartRequest;
  }

  function getSubtotalCents(cart) {
    if (!cart) return 0;
    // Always use original (pre-discount) line prices so that a stale Shopify
    // discount still in the session doesn't lower the cart value used for
    // coupon eligibility and the "Cart value" display.
    if (Array.isArray(cart.items) && cart.items.length > 0) {
      return cart.items.reduce(function (sum, item) {
        return sum + Number(item.original_line_price || item.line_price || (item.price || 0) * (item.quantity || 1));
      }, 0);
    }
    return Number(cart.original_total_price || cart.items_subtotal_price || cart.total_price || 0);
  }

  function getAppliedDiscountCandidates(cart) {
    var seen = {};
    var values = [];
    if (!cart) return values;

    if (Array.isArray(cart.cart_level_discount_applications)) {
      cart.cart_level_discount_applications.forEach(function (app) {
        var val = String((app && (app.title || app.code)) || '').trim();
        var key = val.toUpperCase();
        if (val && !seen[key]) {
          seen[key] = true;
          values.push(val);
        }
      });
    }

    if (Array.isArray(cart.items)) {
      cart.items.forEach(function (item) {
        if (!Array.isArray(item.discounts)) return;
        item.discounts.forEach(function (d) {
          var val = String((d && (d.title || d.code)) || '').trim();
          var key = val.toUpperCase();
          if (val && !seen[key]) {
            seen[key] = true;
            values.push(val);
          }
        });
      });
    }

    return values;
  }

  // Strip STALE code-based discounts from a cart object (mutates in-place).
  // A discount is "stale" if it's a discount_code type that doesn't match any
  // user-applied code in the current session. Preserves:
  //   - Shopify automatic discounts (type === 'automatic')
  //   - Discounts matching an active user-applied code (workerAppliedCodes or optimisticAppliedCode)
  // Returns true if it actually stripped something, false if cart was already clean.
  function stripCodeDiscounts(cart) {
    if (!cart) return false;

    // Check if any code discount matches an active user-applied code (check ALL sources)
    var activeCode = optimisticAppliedCode || (workerAppliedCodes.length > 0 ? workerAppliedCodes[0] : '');
    if (!activeCode) {
      var sessionCodes = getSessionAppliedCodes();
      for (var sci = 0; sci < sessionCodes.length; sci++) {
        var sc = String(sessionCodes[sci]).trim().toUpperCase();
        if (sc && !isCodeSuppressed(sc)) { activeCode = sc; break; }
      }
    }

    // Find code-based discount applications at cart level
    var codeApps = [];
    if (Array.isArray(cart.cart_level_discount_applications)) {
      codeApps = cart.cart_level_discount_applications.filter(function (app) {
        return app && app.type === 'discount_code';
      });
    }

    // Also detect stale code discounts via item-level price discrepancy.
    // If items show final_line_price < original_line_price but we have no active
    // coupon, a stale Shopify discount is still in the session.
    var hasItemLevelDiscount = false;
    if (codeApps.length === 0 && !activeCode && Array.isArray(cart.items)) {
      hasItemLevelDiscount = cart.items.some(function (item) {
        var orig = Number(item.original_line_price || 0);
        var fin = Number(item.final_line_price != null ? item.final_line_price : orig);
        // Check if any discount_allocation is code-based
        var hasCodeAlloc = Array.isArray(item.discount_allocations) && item.discount_allocations.some(function (a) {
          return a && a.discount_application && a.discount_application.type === 'discount_code';
        });
        return hasCodeAlloc || (orig > fin && Array.isArray(item.discounts) && item.discounts.some(function (d) {
          // Non-automatic discounts at item level indicate a stale code discount
          return d && d.type !== 'automatic';
        }));
      });
    }

    if (codeApps.length === 0 && !hasItemLevelDiscount) return false;

    // If Shopify's discount matches our active code, keep it
    if (activeCode && codeApps.length > 0) {
      var hasActiveMatch = codeApps.some(function (app) {
        return String(app.title || '').trim().toUpperCase() === activeCode;
      });
      if (hasActiveMatch) return false;
    }

    // Stale discount — strip it
    var keepApps = [];
    if (Array.isArray(cart.cart_level_discount_applications)) {
      keepApps = cart.cart_level_discount_applications.filter(function (app) {
        return app && app.type !== 'discount_code';
      });
    }

    if (Array.isArray(cart.items)) {
      cart.items.forEach(function (item) {
        var origLine = Number(item.original_line_price || item.line_price || (item.price || 0) * (item.quantity || 1));
        var qty = item.quantity || 1;
        item.final_line_price = origLine;
        item.line_price = origLine;
        item.final_price = qty > 0 ? Math.round(origLine / qty) : 0;
        if (Array.isArray(item.discounts)) {
          item.discounts = item.discounts.filter(function (d) {
            return d && d.type === 'automatic';
          });
        }
        if (Array.isArray(item.discount_allocations)) {
          item.discount_allocations = item.discount_allocations.filter(function (a) {
            return a && a.discount_application && a.discount_application.type !== 'discount_code';
          });
        }
      });
    }

    var origTotal = 0;
    if (Array.isArray(cart.items)) {
      origTotal = cart.items.reduce(function (sum, item) {
        return sum + Number(item.original_line_price || item.line_price || 0);
      }, 0);
    }
    var autoDiscountTotal = keepApps.reduce(function (sum, app) {
      return sum + Number(app.total_allocated_amount || 0);
    }, 0);
    cart.total_price = Math.max(origTotal - autoDiscountTotal, 0);
    cart.total_discount = autoDiscountTotal;
    cart.cart_level_discount_applications = keepApps;

    return true;
  }

  function isCouponCodeInCart(cart, code) {
    if (!cart || !code) return false;
    var normalized = code.trim().toUpperCase();
    return getAppliedDiscountCandidates(cart).some(function (c) {
      return String(c).trim().toUpperCase() === normalized;
    });
  }

  function hasAppliedDiscount(cart) {
    if (!cart) return false;
    if (Number(cart.total_discount || cart.total_discounts || 0) > 0) return true;
    if (Array.isArray(cart.cart_level_discount_applications) && cart.cart_level_discount_applications.length > 0) return true;
    if (Array.isArray(cart.items)) {
      return cart.items.some(function (item) {
        return Array.isArray(item.discounts) && item.discounts.length > 0;
      });
    }
    return false;
  }

  function getAppliedDiscountSavings(cart) {
    var total = Number(cart && (cart.total_discount || cart.total_discounts) || 0);
    if (total > 0) return total;
    if (!cart || !Array.isArray(cart.items)) return 0;
    return cart.items.reduce(function (sum, item) {
      var original = Number(item && (item.original_line_price || item.line_price) || 0);
      var final = Number(item && (item.final_line_price || item.line_price) || 0);
      return sum + Math.max(original - final, 0);
    }, 0);
  }

  // ---------------------------------------------------------------------------
  // Coupon decoration & collections
  // ---------------------------------------------------------------------------
  function decorateCoupon(coupon, subtotalCents, currency) {
    var minimumCents = coupon.minimumCartValue * 100;
    var savingsCents = 0;
    var unlockCents = 0;
    var unlockQuantity = 0;
    var isAvailable = false;
    var statusText = '';

    var rule = getCouponRule(coupon.code);
    var ruleKind = rule ? rule.kind : (coupon.discountKind === 'bxgy' ? 'bxgy' : 'basic');
    var scopeLimited = rule ? !(rule.entitled && rule.entitled.allItems) : (coupon.scopeType !== 'AllDiscountItems');

    // Skip expired or not-yet-active discounts
    if (rule && rule.status === 'EXPIRED') {
      return {
        id: coupon.id, code: coupon.code,
        title: buildCouponTitle(coupon, currency),
        description: buildCouponDescription(coupon, currency),
        minimumCents: 0, savingsCents: 0,
        unlockCents: 0, unlockQuantity: 0,
        available: false, statusText: 'This offer has expired',
        tag: coupon.tag, displaySurface: coupon.displaySurface,
        cardDescription: coupon.cardDescription || '', cardOffer: coupon.cardOffer || ''
      };
    }
    if (rule && rule.startsAt && new Date(rule.startsAt) > new Date()) {
      return {
        id: coupon.id, code: coupon.code,
        title: buildCouponTitle(coupon, currency),
        description: buildCouponDescription(coupon, currency),
        minimumCents: 0, savingsCents: 0,
        unlockCents: 0, unlockQuantity: 0,
        available: false, statusText: 'This offer is not active yet',
        tag: coupon.tag, displaySurface: coupon.displaySurface,
        cardDescription: coupon.cardDescription || '', cardOffer: coupon.cardOffer || ''
      };
    }

    // Free shipping
    if (rule && rule.kind === 'free_shipping') {
      // Check free shipping minimum requirement from Shopify rule
      var freeShipMin = rule.minimumRequirement;
      if (freeShipMin && freeShipMin.type === 'subtotal') {
        var freeShipMinCents = freeShipMin.amount * 100;
        if (subtotalCents < freeShipMinCents) {
          var freeShipUnlock = freeShipMinCents - subtotalCents;
          return {
            id: coupon.id, code: coupon.code,
            title: buildCouponTitle(coupon, currency),
            description: buildCouponDescription(coupon, currency),
            minimumCents: freeShipMinCents, savingsCents: 0,
            unlockCents: freeShipUnlock, unlockQuantity: 0,
            available: false, freeShipping: true,
            statusText: (state.config.unlockPrefix || 'Add') + ' ' + formatMoney(freeShipUnlock, currency) + ' ' + (state.config.moreToAvailSuffix || 'more to avail this offer'),
            tag: coupon.tag, displaySurface: coupon.displaySurface,
        cardDescription: coupon.cardDescription || '', cardOffer: coupon.cardOffer || ''
          };
        }
      }
      isAvailable = true;
      savingsCents = 0;
      statusText = 'Free shipping on this order';
      return {
        id: coupon.id, code: coupon.code,
        title: buildCouponTitle(coupon, currency),
        description: buildCouponDescription(coupon, currency),
        minimumCents: minimumCents, savingsCents: savingsCents,
        unlockCents: 0, unlockQuantity: 0,
        available: isAvailable, freeShipping: true,
        statusText: statusText, tag: coupon.tag, displaySurface: coupon.displaySurface,
        cardDescription: coupon.cardDescription || '', cardOffer: coupon.cardOffer || ''
      };
    }

    // Minimum cart value gate — use Shopify rule's minimumRequirement if available,
    // otherwise fall back to metaobject minimumCartValue
    if (rule && rule.minimumRequirement) {
      if (rule.minimumRequirement.type === 'subtotal') {
        var ruleMinCents = rule.minimumRequirement.amount * 100;
        if (ruleMinCents > minimumCents) minimumCents = ruleMinCents;
      } else if (rule.minimumRequirement.type === 'quantity') {
        var totalQty = 0;
        if (state.cart && Array.isArray(state.cart.items)) {
          state.cart.items.forEach(function (item) { totalQty += (item.quantity || 0); });
        }
        if (totalQty < rule.minimumRequirement.quantity) {
          var neededQty = rule.minimumRequirement.quantity - totalQty;
          return {
            id: coupon.id, code: coupon.code,
            title: buildCouponTitle(coupon, currency),
            description: buildCouponDescription(coupon, currency),
            minimumCents: 0, savingsCents: 0,
            unlockCents: 0, unlockQuantity: neededQty,
            available: false,
            statusText: 'Add ' + neededQty + ' more item' + (neededQty === 1 ? '' : 's') + ' to avail this offer',
            tag: coupon.tag, displaySurface: coupon.displaySurface,
        cardDescription: coupon.cardDescription || '', cardOffer: coupon.cardOffer || ''
          };
        }
      }
    }

    if (minimumCents > 0 && subtotalCents < minimumCents) {
      unlockCents = minimumCents - subtotalCents;
      statusText = (state.config.unlockPrefix || 'Add') + ' ' + formatMoney(unlockCents, currency) + ' ' + (state.config.moreToAvailSuffix || 'more to avail this offer');
      return {
        id: coupon.id, code: coupon.code,
        title: buildCouponTitle(coupon, currency),
        description: buildCouponDescription(coupon, currency),
        minimumCents: minimumCents, savingsCents: 0,
        unlockCents: unlockCents, unlockQuantity: 0,
        available: false, statusText: statusText,
        tag: coupon.tag, displaySurface: coupon.displaySurface,
        cardDescription: coupon.cardDescription || '', cardOffer: coupon.cardOffer || ''
      };
    }

    if (ruleKind === 'bxgy') {
      // BXGY: check customerBuys requirement
      var buysMet;

      if (rule) {
        // Worker rule loaded — use precise product-level evaluation
        buysMet = meetsCustomerBuysRequirement(rule, state.cart);
      } else {
        // Rule not loaded yet — fall back to metaobject minimumCartValue
        // This allows BXGY coupons to show as available based on cart total
        // until the Worker provides exact product-level requirements
        buysMet = minimumCents === 0 || subtotalCents >= minimumCents;
        // Also check metaobject requirementValue for quantity-based BXGY
        if (buysMet && coupon.requirementType === 'quantity' && coupon.requirementValue > 0) {
          var fallbackQty = 0;
          if (state.cart && Array.isArray(state.cart.items)) {
            state.cart.items.forEach(function (item) { fallbackQty += (item.quantity || 0); });
          }
          buysMet = fallbackQty >= coupon.requirementValue;
        }
      }

      if (!buysMet) {
        // Check if this is an amount-based or quantity-based requirement
        if (rule && rule.buysAmount && rule.buysAmount > 0) {
          var neededCents = Math.max(rule.buysAmount * 100 - subtotalCents, 0);
          unlockCents = neededCents;
          statusText = (state.config.unlockPrefix || 'Add') + ' ' + formatMoney(neededCents, currency) + ' ' + (state.config.moreToAvailSuffix || 'more to avail this offer');
        } else if (rule && rule.buysQuantity && rule.buysQuantity > 0) {
          var requiredQty = rule.buysQuantity;
          var matchedQty = 0;
          if (state.cart && Array.isArray(state.cart.items) && rule.customerBuys) {
            state.cart.items.forEach(function (item) {
              if (isItemEligible(item, rule.customerBuys)) matchedQty += (item.quantity || 0);
            });
          }
          unlockQuantity = Math.max(requiredQty - matchedQty, 1);
          statusText = 'Add ' + formatCount(unlockQuantity) + ' more eligible item' + (unlockQuantity === 1 ? '' : 's') + ' to avail this offer';
        } else if (!rule && minimumCents > 0 && subtotalCents < minimumCents) {
          // No rule loaded — use metaobject minimum as fallback unlock message
          unlockCents = minimumCents - subtotalCents;
          statusText = (state.config.unlockPrefix || 'Add') + ' ' + formatMoney(unlockCents, currency) + ' ' + (state.config.moreToAvailSuffix || 'more to avail this offer');
        } else {
          // Fallback: quantity-based from metaobject
          var fbRequiredQty = coupon.requirementValue || 1;
          var fbMatchedQty = 0;
          if (state.cart && Array.isArray(state.cart.items)) {
            state.cart.items.forEach(function (item) { fbMatchedQty += (item.quantity || 0); });
          }
          unlockQuantity = Math.max(fbRequiredQty - fbMatchedQty, 1);
          statusText = 'Add ' + formatCount(unlockQuantity) + ' more eligible item' + (unlockQuantity === 1 ? '' : 's') + ' to avail this offer';
        }
      } else {
        // Calculate actual savings using rule-aware function
        var bxgyResult = calculateItemDiscounts(coupon, state.cart);
        savingsCents = bxgyResult.totalDiscount;
        isAvailable = true;
        if (savingsCents > 0) {
          statusText = (state.config.savePrefix || 'Save') + ' ' + formatMoney(savingsCents, currency) + ' ' + (state.config.saveOnOrderLabel || 'on this order');
        } else {
          var rewardQty = Math.max((rule && rule.rewardQuantity) || coupon.rewardQuantity || 1, 1);
          statusText = 'Add ' + formatCount(rewardQty) + ' eligible reward item' + (rewardQty === 1 ? '' : 's') + ' to your cart';
        }
      }
    } else {
      // Basic discount — use rule-aware calculation (handles scoped products/collections)
      var basicResult = calculateItemDiscounts(coupon, state.cart);
      savingsCents = basicResult.totalDiscount;

      if (savingsCents > 0) {
        isAvailable = true;
        var prefix = (state.config.savePrefix || 'Save') + (scopeLimited ? ' up to ' : ' ');
        statusText = prefix + formatMoney(savingsCents, currency) + ' ' + (state.config.saveOnOrderLabel || 'on this order');
      } else {
        // No eligible items in cart for this coupon's scope
        statusText = 'Add eligible items to avail this offer';
      }
    }

    return {
      id: coupon.id,
      code: coupon.code,
      title: buildCouponTitle(coupon, currency),
      description: buildCouponDescription(coupon, currency),
      minimumCents: minimumCents,
      savingsCents: savingsCents,
      unlockCents: unlockCents,
      unlockQuantity: unlockQuantity,
      available: isAvailable,
      statusText: statusText,
      tag: coupon.tag,
      displaySurface: coupon.displaySurface,
      cardDescription: coupon.cardDescription || '',
      cardOffer: coupon.cardOffer || ''
    };
  }

  function buildCouponTitle(coupon, currency) {
    if (coupon.offerTitle) return coupon.offerTitle;

    if (coupon.discountKind === 'bxgy') {
      return coupon.discountValue >= 100 ? 'Free gift offer' : 'Buy X Get Y offer';
    }
    if (coupon.discountType === 'fixed') {
      return 'Flat ' + formatMoney(coupon.discountValue * 100, currency) + ' off';
    }
    return formatDiscountValue(coupon.discountValue) + '% off';
  }

  function buildCouponDescription(coupon, currency) {
    if (coupon.offerDescription) return coupon.offerDescription;

    if (coupon.discountKind === 'bxgy') {
      if (coupon.requirementType === 'subtotal') {
        return 'Spend ' + formatMoney(coupon.requirementValue * 100, currency) + ' to unlock';
      }
      if (coupon.requirementType === 'quantity') {
        return 'Buy ' + formatCount(coupon.requirementValue) + ' eligible items to unlock';
      }
      return 'Add eligible items to unlock';
    }

    if (coupon.scopeType !== 'AllDiscountItems') return 'On eligible items only';
    if (coupon.minimumCartValue > 0) return 'on orders above ' + formatMoney(coupon.minimumCartValue * 100, currency);
    return 'on all orders';
  }

  function compareAvailable(a, b) {
    if (b.savingsCents !== a.savingsCents) return b.savingsCents - a.savingsCents;
    return a.code.localeCompare(b.code);
  }

  function compareUnavailable(a, b) {
    if (a.unlockCents !== b.unlockCents) return a.unlockCents - b.unlockCents;
    return a.code.localeCompare(b.code);
  }

  function buildCollections(cart) {
    var subtotalCents = getSubtotalCents(cart);
    var currency = getCurrency(cart);

    var coupons = state.config.coupons.map(function (c) {
      return decorateCoupon(c, subtotalCents, currency);
    });

    var drawerCoupons = coupons.filter(function (c) {
      return c.displaySurface === 'both' || c.displaySurface === 'popup';
    });
    var previewCoupons = drawerCoupons.filter(function (c) {
      return c.displaySurface === 'both';
    });

    var available = drawerCoupons.filter(function (c) { return c.available; }).sort(compareAvailable);
    var unavailable = drawerCoupons.filter(function (c) { return !c.available; }).sort(compareUnavailable);
    var all = available.concat(unavailable);

    var previewAvailable = previewCoupons.filter(function (c) { return c.available; }).sort(compareAvailable);

    var best = available[0] || null;
    var previewBest = previewAvailable[0] || null;

    var brand = all.filter(function (c) { return c.tag === 'brand'; });

    var moreCount = drawerCoupons.length - 1;
    if (moreCount < 0) moreCount = 0;

    return {
      currency: currency,
      subtotalCents: subtotalCents,
      all: all,
      available: available,
      unavailable: unavailable,
      brand: brand,
      best: best,
      previewBest: previewBest,
      moreCount: moreCount
    };
  }

  // ---------------------------------------------------------------------------
  // Applied coupon detection
  // ---------------------------------------------------------------------------
  function isCouponApplied(coupon) {
    if (isCodeSuppressed(coupon.code)) return false;

    // Check optimistic applied code first
    if (optimisticAppliedCode && coupon.code === optimisticAppliedCode) return true;

    // Check Worker-backed applied codes (source of truth)
    if (workerAppliedCodes.indexOf(coupon.code) !== -1) return true;

    var candidates = getAppliedDiscountCandidates(state.cart);
    var normalized = coupon.code.replace(/[\s\-_]/g, '');
    return candidates.some(function (c) {
      var nc = String(c).trim().toUpperCase();
      return nc === coupon.code || nc.replace(/[\s\-_]/g, '') === normalized;
    });
  }

  function getAppliedPreviewState(collections) {
    // Check optimistic applied code first
    if (optimisticAppliedCode) {
      var optimisticMatch = collections.all.find(function (c) { return c.code === optimisticAppliedCode; });
      if (optimisticMatch) {
        return {
          coupon: optimisticMatch,
          code: optimisticMatch.code,
          savingsCents: optimisticMatch.savingsCents
        };
      }
    }

    // Check Worker-backed applied codes (source of truth)
    if (workerAppliedCodes.length > 0) {
      var workerCode = workerAppliedCodes[0];
      var workerMatch = collections.all.find(function (c) { return c.code === workerCode; });
      if (workerMatch && !isCodeSuppressed(workerCode)) {
        return {
          coupon: workerMatch,
          code: workerMatch.code,
          savingsCents: workerMatch.savingsCents
        };
      }
    }

    if (!hasAppliedDiscount(state.cart)) return null;

    var candidates = getAppliedDiscountCandidates(state.cart);

    // Filter out suppressed codes
    var activeCandidates = candidates.filter(function (c) {
      return !isCodeSuppressed(String(c).trim().toUpperCase());
    });
    if (activeCandidates.length === 0) return null;

    var matched = collections.all.find(function (coupon) {
      if (isCodeSuppressed(coupon.code)) return false;
      var normalized = coupon.code.replace(/[\s\-_]/g, '');
      return activeCandidates.some(function (c) {
        var nc = String(c).trim().toUpperCase();
        return nc === coupon.code || nc.replace(/[\s\-_]/g, '') === normalized;
      });
    }) || null;

    var savingsCents = getAppliedDiscountSavings(state.cart);
    if (matched && savingsCents <= 0) savingsCents = matched.savingsCents;

    return {
      coupon: matched,
      code: matched ? matched.code : (activeCandidates[0] || ''),
      savingsCents: savingsCents
    };
  }

  // ---------------------------------------------------------------------------
  // Preview bar rendering
  // ---------------------------------------------------------------------------
  function getPreviewIconMarkup(className) {
    if (state.config && state.config.previewIconUrl) {
      return '<img class="' + className + '" src="' + escapeHtml(state.config.previewIconUrl) + '" alt="" width="20" height="20">';
    }
    return '<span class="' + className + '">' + getCardIconMarkup() + '</span>';
  }

  function getOfferLabel(collections) {
    var count = collections.available.length;
    if (count <= 0) return '';
    return count + ' offer' + (count === 1 ? '' : 's') + ' available';
  }

  function getMoreOffersLabel(count) {
    if (count <= 0) return 'View Coupons';
    return '+' + count + ' ' + escapeHtml(state.config.previewOffersSuffix || 'more offers');
  }

  function renderPreview(collections) {
    var hosts = document.querySelectorAll('[data-cart-coupon-host]');
    var hasApplied = hasAppliedDiscount(state.cart);
    var hasAvailable = collections.available.length > 0;
    var hasContent = state.config && collections.all.length > 0 && state.cart && state.cart.item_count > 0 && (hasApplied || hasAvailable);

    document.body.classList.toggle('cart-coupons-active', Boolean(state.config));

    hosts.forEach(function (host) {
      if (!hasContent) {
        host.innerHTML = '';
        host.hidden = true;
        return;
      }

      var best = collections.previewBest;
      var applied = getAppliedPreviewState(collections);

      if (!best && !applied) {
        var collapseLabel = getOfferLabel(collections) || 'View Coupons';
        host.innerHTML = [
          '<div class="cart-coupon-preview cart-coupon-preview--collapsed" data-cart-coupon-open>',
            getPreviewIconMarkup('cart-coupon-preview__collapsed-icon'),
            '<span class="cart-coupon-preview__collapsed-label">' + escapeHtml(collapseLabel) + '</span>',
            '<span class="cart-coupon-preview__collapsed-arrow">' + arrowIcon + '</span>',
          '</div>'
        ].join('');
        host.hidden = false;
        return;
      }

      var eyebrowText = '';
      var withCode = '';
      var applyMarkup = '';
      var ctaLeftLabel = '';

      if (applied) {
        eyebrowText = formatMoney(applied.savingsCents, collections.currency) + ' Savings';
        withCode = applied.code;
        applyMarkup = '<button class="cart-coupon-preview__apply is-remove" type="button" data-cart-coupon-clear>Remove</button>';
        ctaLeftLabel = getMoreOffersLabel(collections.moreCount);
      } else if (best) {
        eyebrowText = (state.config.savePrefix || 'Save') + ' ' + formatMoney(best.savingsCents, collections.currency);
        withCode = best.code;
        applyMarkup = '<button class="cart-coupon-preview__apply" type="button" data-cart-coupon-apply="' + escapeHtml(best.code) + '">' + escapeHtml(state.config.applyLabel || 'Apply') + '</button>';
        ctaLeftLabel = getMoreOffersLabel(collections.moreCount);
      }

      var withLineMarkup = '';
      if (withCode) {
        withLineMarkup = [
          '<div class="cart-coupon-preview__withline">',
            escapeHtml(state.config.withPrefix || 'With'),
            ' \'<span class="cart-coupon-preview__withline-code">' + escapeHtml(withCode) + '</span>\'',
            ' <span class="cart-coupon-preview__withline-chevron">' + chevronDownIcon + '</span>',
          '</div>'
        ].join('');
      }

      host.innerHTML = [
        '<div class="cart-coupon-preview">',
          '<div class="cart-coupon-preview__top">',
            '<div class="cart-coupon-preview__meta">',
              '<div class="cart-coupon-preview__offerline is-save">',
                getPreviewIconMarkup('cart-coupon-preview__offericon'),
                '<span>' + escapeHtml(eyebrowText) + '</span>',
              '</div>',
              withLineMarkup,
            '</div>',
            applyMarkup,
          '</div>',
          '<button class="cart-coupon-preview__cta" type="button" data-cart-coupon-open>',
            '<span class="cart-coupon-preview__cta-prefix">',
              getPreviewIconMarkup('cart-coupon-preview__cta-icon'),
              '<span class="cart-coupon-preview__cta-label">' + escapeHtml(ctaLeftLabel) + '</span>',
            '</span>',
            '<span class="cart-coupon-preview__cta-link">',
              'Enter a coupon code',
              ' <span class="cart-coupon-preview__cta-arrow">' + arrowIcon + '</span>',
            '</span>',
          '</button>',
        '</div>'
      ].join('');

      host.hidden = false;
    });
  }

  // ---------------------------------------------------------------------------
  // Drawer rendering
  // ---------------------------------------------------------------------------
  function buildCardMarkup(coupon, isBest, currency) {
    var badgeMarkup = isBest ? '<span class="cart-coupon-card__badge">' + escapeHtml(state.config.bestDealLabel || 'Best deal') + '</span>' : '';
    var statusText = escapeHtml(coupon.statusText || '');
    var isApplied = isCouponApplied(coupon);
    var buttonMarkup;

    if (isApplied) {
      buttonMarkup = '<button class="cart-coupon-card__button is-applied" type="button" disabled>Applied</button>';
    } else if (coupon.available) {
      buttonMarkup = '<button class="cart-coupon-card__button" type="button" data-cart-coupon-apply="' + escapeHtml(coupon.code) + '">' + escapeHtml(state.config.applyLabel || 'Apply') + '</button>';
    } else {
      buttonMarkup = '<button class="cart-coupon-card__button is-disabled" type="button" disabled>' + escapeHtml(state.config.lockedLabel || 'Locked') + '</button>';
    }

    var cardClass = 'cart-coupon-card';
    if (isApplied) cardClass += ' is-applied';
    else if (!coupon.available) cardClass += ' is-unavailable';
    else if (isBest) cardClass += ' is-best';

    // Use custom card text fields; fall back to auto-generated title/description
    var descriptionText = coupon.cardDescription || coupon.description || '';
    var offerText = coupon.cardOffer || coupon.title || '';

    // Status line: green if available, red if locked/unavailable
    var statusClass = 'cart-coupon-card__status';
    if (coupon.available || isApplied) {
      statusClass += ' is-available';
    } else {
      statusClass += ' is-unavailable';
    }

    return [
      '<article class="' + cardClass + '">',
      '<div class="cart-coupon-card__body">',
      '<div class="cart-coupon-card__code-row">',
      '<span class="cart-coupon-card__icon">' + getCardIconMarkup() + '</span>',
      '<span class="cart-coupon-card__code">' + escapeHtml(coupon.code) + '</span>',
      badgeMarkup,
      '</div>',
      descriptionText ? '<p class="cart-coupon-card__description">' + escapeHtml(descriptionText) + '</p>' : '',
      offerText ? '<p class="cart-coupon-card__title">' + escapeHtml(offerText) + '</p>' : '',
      statusText ? '<p class="' + statusClass + '">' + statusText + '</p>' : '',
      '</div>',
      '<div class="cart-coupon-card__action">',
      buttonMarkup,
      '</div>',
      '</article>'
    ].join('');
  }

  function renderDrawer(collections) {
    var cartValueEl = document.querySelector('[data-cart-coupon-cart-value]');
    if (cartValueEl) {
      cartValueEl.textContent = 'Cart value: ' + formatMoney(collections.subtotalCents, collections.currency);
    }

    renderTabs();

    var list = getList();
    if (!list) return;

    var coupons;
    if (state.activeTab === 'brand') {
      coupons = collections.brand;
    } else if (state.activeTab === 'unavailable') {
      coupons = collections.unavailable;
    } else {
      coupons = collections.all;
    }

    if (!coupons.length) {
      list.innerHTML = '<div class="cart-coupon-sheet__empty">' + escapeHtml(state.config.emptyStateLabel || 'No coupons available right now.') + '</div>';
      return;
    }

    list.innerHTML = '<div class="cart-coupon-sheet__list">' + coupons.map(function (coupon) {
      return buildCardMarkup(coupon, collections.best && collections.best.code === coupon.code && collections.best.available === coupon.available, collections.currency);
    }).join('') + '</div>';
  }

  function renderTabs() {
    getTabButtons().forEach(function (btn) {
      var isActive = btn.getAttribute('data-cart-coupon-tab') === state.activeTab;
      btn.classList.toggle('is-active', isActive);
      btn.setAttribute('aria-selected', isActive ? 'true' : 'false');
    });
  }

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  function clearCoupons() {
    document.body.classList.remove('cart-coupons-active');
    document.querySelectorAll('[data-cart-coupon-host]').forEach(function (host) {
      host.innerHTML = '';
      host.hidden = true;
    });
    closeDrawer(true);
    closeSavingsPopup(true);
  }

  function render() {
    if (!state.config || !state.cart) {
      clearCoupons();
      return;
    }

    var collections = buildCollections(state.cart);
    renderPreview(collections);
    renderDrawer(collections);
    maybeShowPendingSavingsPopup(collections);
  }

  // ---------------------------------------------------------------------------
  // Cart fetching & refresh
  // ---------------------------------------------------------------------------
  function refreshCoupons(force) {
    if (!state.config) {
      clearCoupons();
      return Promise.resolve();
    }

    if (!force && state.cart) {
      render();
      return Promise.resolve();
    }

    return requestCart(force)
      .then(function (cart) {
        var previousCart = state.cart;
        var didStrip = stripCodeDiscounts(cart);
        state.cart = cart;
        clearAutoAppliedDiscounts(cart);
        render();
        if (didStrip || getAppliedCouponConfig()) {
          syncCartWithCoupon();
        }
        maybeShowTransitionSavingsPopup(previousCart, cart);
        state.lastCartAction = '';
      })
      .catch(function (error) {
        state.lastCartAction = '';
        console.error('Cart coupons refresh failed.', error);
      });
  }

  function scheduleRefresh(delay) {
    clearTimeout(state.refreshTimer);
    state.refreshTimer = setTimeout(function () {
      refreshCoupons(true);
    }, delay || 350);
  }

  function handleExternalCartUpdate(cart) {
    var previousCart = state.cart;
    if (!state.config || !cart || typeof cart.item_count === 'undefined') return;

    // Ignore our own patched carts sent back from theme.js — prevents double-patching
    if (cart._vayronPatched) return;

    // Strip stale code-based discounts from fresh Shopify cart data
    var didStrip = stripCodeDiscounts(cart);

    clearTimeout(state.refreshTimer);
    state.refreshTimer = null;
    state.cart = cart;

    // Auto-remove applied coupon if cart no longer meets eligibility (e.g. user reduced qty)
    maybeAutoRemoveIneligibleCoupon(cart);

    render();

    // Only push back to theme.js if we actually stripped discounts or user has a coupon applied.
    // Otherwise let theme.js handle its own rendering — avoids price flicker on +/- buttons.
    if (didStrip || getAppliedCouponConfig()) {
      syncCartWithCoupon();
    }

    maybeShowTransitionSavingsPopup(previousCart, cart);
    state.lastCartAction = '';
  }

  // ---------------------------------------------------------------------------
  // Auto-remove coupon when cart no longer meets eligibility
  // ---------------------------------------------------------------------------
  function maybeAutoRemoveIneligibleCoupon(cart) {
    if (!cart || !state.config || state.applyingCode || state.removingCode) return;

    // Find the currently applied code
    var appliedCode = optimisticAppliedCode || (workerAppliedCodes.length > 0 ? workerAppliedCodes[0] : '');
    if (!appliedCode || isCodeSuppressed(appliedCode)) return;

    // Find its config entry
    var couponConfig = null;
    state.config.coupons.forEach(function (c) {
      if (c.code === appliedCode) couponConfig = c;
    });
    if (!couponConfig) return;

    // Decorate to check current eligibility
    var subtotalCents = getSubtotalCents(cart);
    var currency = getCurrency(cart);
    var decorated = decorateCoupon(couponConfig, subtotalCents, currency);

    // If no longer available, auto-remove
    if (!decorated.available) {
      // Suppress + remove from applied lists
      addSuppressedCode(appliedCode);
      removeUserAppliedCode(appliedCode);
      removeSessionAppliedCode(appliedCode);
      if (optimisticAppliedCode === appliedCode) optimisticAppliedCode = null;

      // Clear Shopify's server-side discount session
      clearDiscountCookieFallback();
      var autoRemoveCode = appliedCode;
      try {
        var iframe = document.createElement('iframe');
        iframe.style.display = 'none';
        var cleared = false;
        var onCleared = function () {
          if (cleared) return;
          cleared = true;
          try { iframe.remove(); } catch (e) {}
          requestCart(true).then(function (freshCart) {
            if (freshCart) {
              stripCodeDiscounts(freshCart);
              state.cart = freshCart;
              render();
              syncCartWithCoupon();
            }
          }).catch(function () {});
        };
        iframe.onload = function () { setTimeout(onCleared, 100); };
        iframe.onerror = onCleared;
        iframe.src = '/discount/%20?redirect=/cart';
        document.body.appendChild(iframe);
        setTimeout(onCleared, 2000);
      } catch (e) {}

      // Notify Worker
      workerRemove(autoRemoveCode).catch(function () {});

      render();
      syncCartWithCoupon();
    }
  }

  // ---------------------------------------------------------------------------
  // Apply coupon — optimistic UI (instant feedback, server sync in background)
  // ---------------------------------------------------------------------------
  var optimisticAppliedCode = null; // Code we've optimistically shown as "applied"

  function applyCouponCode(code) {
    if (!code || state.applyingCode) return Promise.resolve();

    state.applyingCode = code;

    var normalizedCode = code.trim().toUpperCase();

    // Block BXGY apply if customerBuys requirement is not met
    var applyRule = getCouponRule(normalizedCode);
    if (applyRule && applyRule.kind === 'bxgy' && !meetsCustomerBuysRequirement(applyRule, state.cart)) {
      state.applyingCode = null;
      return Promise.resolve();
    }

    // Enforce single-coupon rule: remove any existing applied coupon first
    var previousCodes = workerAppliedCodes.slice();
    previousCodes.forEach(function (oldCode) {
      if (oldCode !== normalizedCode) {
        addSuppressedCode(oldCode);
        removeUserAppliedCode(oldCode);
        removeSessionAppliedCode(oldCode);
      }
    });

    // Instant: show this coupon as "applied" and close drawer
    optimisticAppliedCode = normalizedCode;
    addUserAppliedCode(normalizedCode);
    removeSuppressedCode(normalizedCode);
    addSessionAppliedCode(normalizedCode);
    closeDrawer();
    render(); // UI instantly shows "Applied" state
    syncCartWithCoupon(); // Push patched cart data to theme.js

    // Calculate savings and show popup immediately (no waiting for Worker)
    var popupSavings = 0;
    try {
      var couponConfig = getAppliedCouponConfig();
      if (couponConfig && state.cart) {
        var discountResult = calculateItemDiscounts(couponConfig, state.cart);
        if (discountResult && discountResult.totalDiscount > 0) {
          popupSavings = discountResult.totalDiscount;
        }
      }
    } catch (e) { /* popup is best-effort */ }

    // Show popup + confetti immediately after optimistic render
    if (popupSavings > 0) {
      showSavingsPopup(popupSavings, normalizedCode);
    }

    // Background: sync to Shopify session immediately via hidden iframe.
    // This way, when user clicks Checkout, the session already has the code — Shopflo reads it without delay.
    try {
      var bgIframe = document.createElement('iframe');
      bgIframe.style.display = 'none';
      bgIframe.src = '/discount/' + encodeURIComponent(normalizedCode) + '?redirect=/cart';
      document.body.appendChild(bgIframe);
      setTimeout(function () { try { bgIframe.remove(); } catch (e) {} }, 3000);
    } catch (e) {}

    // Background: notify Worker (replaces any existing code — no stacking)
    return workerApply(normalizedCode)
      .then(function (result) {
        state.applyingCode = null;
        optimisticAppliedCode = null;
        if (result && Array.isArray(result.codes)) {
          workerAppliedCodes = result.codes;
        }
        render();
        syncCartWithCoupon();
      })
      .catch(function (error) {
        console.error('Coupon apply failed:', error);
        state.applyingCode = null;
        optimisticAppliedCode = null;
        removeUserAppliedCode(normalizedCode);
        removeSessionAppliedCode(normalizedCode);
        render();
        syncCartWithCoupon();
      });
  }

  // ---------------------------------------------------------------------------
  // sessionStorage: suppressed codes (for instant UI removal)
  // ---------------------------------------------------------------------------
  var SUPPRESSED_KEY = 'vayronCartCouponSuppressed';

  function getSuppressedCodes() {
    try { return JSON.parse(window.sessionStorage.getItem(SUPPRESSED_KEY)) || []; }
    catch (e) { return []; }
  }

  function addSuppressedCode(code) {
    var normalized = String(code).trim().toUpperCase();
    if (!normalized) return;
    var codes = getSuppressedCodes();
    if (codes.indexOf(normalized) === -1) codes.push(normalized);
    try { window.sessionStorage.setItem(SUPPRESSED_KEY, JSON.stringify(codes)); }
    catch (e) {}
  }

  function removeSuppressedCode(code) {
    var normalized = String(code).trim().toUpperCase();
    var codes = getSuppressedCodes().filter(function (c) { return c !== normalized; });
    try { window.sessionStorage.setItem(SUPPRESSED_KEY, JSON.stringify(codes)); }
    catch (e) {}
  }

  function isCodeSuppressed(code) {
    return getSuppressedCodes().indexOf(String(code).trim().toUpperCase()) !== -1;
  }

  // ---------------------------------------------------------------------------
  // sessionStorage: applied codes (survives same-tab navigation including checkout→back)
  // ---------------------------------------------------------------------------
  var SESSION_APPLIED_KEY = 'vayronCartCouponApplied';

  function getSessionAppliedCodes() {
    try { return JSON.parse(window.sessionStorage.getItem(SESSION_APPLIED_KEY)) || []; }
    catch (e) { return []; }
  }

  function addSessionAppliedCode(code) {
    var normalized = String(code).trim().toUpperCase();
    if (!normalized) return;
    var codes = getSessionAppliedCodes();
    if (codes.indexOf(normalized) === -1) codes.push(normalized);
    try { window.sessionStorage.setItem(SESSION_APPLIED_KEY, JSON.stringify(codes)); }
    catch (e) {}
  }

  function removeSessionAppliedCode(code) {
    var normalized = String(code).trim().toUpperCase();
    var codes = getSessionAppliedCodes().filter(function (c) { return c !== normalized; });
    try { window.sessionStorage.setItem(SESSION_APPLIED_KEY, JSON.stringify(codes)); }
    catch (e) {}
  }

  function clearSessionAppliedCodes() {
    try { window.sessionStorage.removeItem(SESSION_APPLIED_KEY); }
    catch (e) {}
  }

  // ---------------------------------------------------------------------------
  // Remove coupon (instant UI + background server clear)
  // ---------------------------------------------------------------------------
  function clearAppliedCode() {
    if (state.removingCode) return;

    var collections = state.cart ? buildCollections(state.cart) : null;
    var applied = collections ? getAppliedPreviewState(collections) : null;
    var codeToRemove = applied ? applied.code : '';

    if (!codeToRemove) return;

    state.removingCode = true;

    // Instant: suppress code in UI + remove from applied list
    addSuppressedCode(codeToRemove);
    removeUserAppliedCode(codeToRemove);
    removeSessionAppliedCode(codeToRemove);
    render(); // Instant UI update — coupon bar shows "Apply" again
    syncCartWithCoupon(); // Push original (unpatched) cart data to theme.js

    // Clear Shopify's server-side discount session so /cart.js stops returning
    // the old discount on subsequent fetches (quantity changes, page loads, etc.)
    clearDiscountCookieFallback();
    try {
      var iframe = document.createElement('iframe');
      iframe.style.display = 'none';
      var cleared = false;
      var onShopifyCleared = function () {
        if (cleared) return;
        cleared = true;
        try { iframe.remove(); } catch (e) {}
        // Re-fetch cart so theme.js gets clean data without stale discount
        requestCart(true).then(function (freshCart) {
          if (freshCart) {
            stripCodeDiscounts(freshCart);
            state.cart = freshCart;
            render();
            syncCartWithCoupon();
          }
        }).catch(function () {});
      };
      iframe.onload = function () { setTimeout(onShopifyCleared, 100); };
      iframe.onerror = onShopifyCleared;
      iframe.src = '/discount/%20?redirect=/cart';
      document.body.appendChild(iframe);
      setTimeout(onShopifyCleared, 2000);
    } catch (e) {}

    // Background: notify Worker (no Shopify interaction)
    workerRemove(codeToRemove)
      .then(function () {
        render();
        syncCartWithCoupon();
      })
      .catch(function (error) {
        console.error('Coupon remove failed:', error);
      })
      .then(function () {
        // Always unlock removingCode when Worker completes (success or fail)
        state.removingCode = false;
      });

    // Safety: unlock removingCode after 5s no matter what (prevents permanent lock)
    setTimeout(function () { state.removingCode = false; }, 5000);
  }

  // ---------------------------------------------------------------------------
  // Override cart prices in DOM based on Worker state
  // ---------------------------------------------------------------------------
  // ---------------------------------------------------------------------------
  // Patch cart data with coupon discount (so theme.js renders correct prices)
  // ---------------------------------------------------------------------------
  function getAppliedCouponConfig() {
    if (!state.config || !state.config.coupons) return null;
    var appliedCodes = getUserAppliedCodes();
    if (appliedCodes.length === 0 && !optimisticAppliedCode) return null;

    var codeToFind = optimisticAppliedCode || appliedCodes[0];
    if (!codeToFind) return null;

    return state.config.coupons.find(function (c) {
      return c.code === codeToFind;
    }) || null;
  }

  // Get original (pre-discount) line price for an item
  function getOriginalLinePrice(item) {
    return Number(item.original_line_price || item.line_price || (item.price || 0) * (item.quantity || 1));
  }

  // Get the original subtotal (sum of all original line prices)
  function getOriginalSubtotal(cart) {
    if (!cart || !Array.isArray(cart.items)) {
      return Number(cart && (cart.items_subtotal_price || cart.original_total_price || cart.total_price) || 0);
    }
    return cart.items.reduce(function (sum, item) {
      return sum + getOriginalLinePrice(item);
    }, 0);
  }

  // Calculate discount for each item and return { totalDiscount, itemDiscounts[], freeShipping }
  // Uses Worker-fetched rule (Shopify source of truth) when available
  function calculateItemDiscounts(coupon, cart) {
    var result = { totalDiscount: 0, itemDiscounts: [], freeShipping: false };
    if (!coupon || !cart || !Array.isArray(cart.items) || cart.items.length === 0) return result;

    var rule = getCouponRule(coupon.code);

    // Enforce Shopify minimum requirement (subtotal or quantity)
    if (rule && rule.minimumRequirement) {
      var cartSubtotal = cart.items.reduce(function (s, item) {
        return s + getOriginalLinePrice(item);
      }, 0);
      if (rule.minimumRequirement.type === 'subtotal' && cartSubtotal < rule.minimumRequirement.amount * 100) {
        result.itemDiscounts = cart.items.map(function () { return 0; });
        return result;
      }
      if (rule.minimumRequirement.type === 'quantity') {
        var cartQty = cart.items.reduce(function (s, item) { return s + (item.quantity || 0); }, 0);
        if (cartQty < rule.minimumRequirement.quantity) {
          result.itemDiscounts = cart.items.map(function () { return 0; });
          return result;
        }
      }
    }

    // Free shipping: no monetary discount in cart preview
    if (rule && rule.kind === 'free_shipping') {
      result.freeShipping = true;
      result.itemDiscounts = cart.items.map(function () { return 0; });
      return result;
    }

    var entitled = rule ? rule.entitled : null;
    var isBxgy = (rule && rule.kind === 'bxgy') || coupon.discountKind === 'bxgy';

    if (isBxgy) {
      // BXGY: customer must meet customerBuys requirement first
      if (rule && !meetsCustomerBuysRequirement(rule, cart)) {
        result.itemDiscounts = cart.items.map(function () { return 0; });
        return result;
      }
      // If rule not loaded for BXGY, we can't know which items are entitled rewards
      // Return 0 discount — never guess at BXGY savings without the Shopify rule
      if (!rule) {
        result.itemDiscounts = cart.items.map(function () { return 0; });
        return result;
      }

      var rewardQty = Math.max((rule && rule.rewardQuantity) || coupon.rewardQuantity || 1, 1);
      var discountValueRaw = (rule && rule.discountValue) || coupon.discountValue || 0;
      var discountPercent = Math.min(discountValueRaw, 100) / 100;

      // Build a list of (itemIndex, unitPrice) for ELIGIBLE items only (rule.entitled),
      // sorted cheapest first
      var units = [];
      cart.items.forEach(function (item, idx) {
        if (!isItemEligible(item, entitled)) return;
        var unitPrice = Number(item.original_price || item.price || 0);
        var qty = item.quantity || 1;
        for (var q = 0; q < qty; q++) {
          units.push({ idx: idx, unitPrice: unitPrice });
        }
      });
      units.sort(function (a, b) { return a.unitPrice - b.unitPrice; });

      var perItem = cart.items.map(function () { return 0; });
      var rewarded = 0;
      for (var u = 0; u < units.length && rewarded < rewardQty; u++) {
        perItem[units[u].idx] += Math.round(units[u].unitPrice * discountPercent);
        rewarded++;
      }

      var bxgyTotal = 0;
      result.itemDiscounts = cart.items.map(function (item, idx) {
        var disc = perItem[idx];
        bxgyTotal += disc;
        return disc;
      });
      result.totalDiscount = bxgyTotal;

    } else {
      // Basic: percentage or fixed amount off — only on eligible items
      var eligibleItems = cart.items.filter(function (item) {
        return isItemEligible(item, entitled);
      });
      var eligibleSubtotal = eligibleItems.reduce(function (sum, item) {
        return sum + getOriginalLinePrice(item);
      }, 0);

      if (eligibleSubtotal === 0) {
        result.itemDiscounts = cart.items.map(function () { return 0; });
        return result;
      }

      var basicType = (rule && rule.discountType) || coupon.discountType;
      var basicValue = (rule && rule.discountValue) || coupon.discountValue || 0;

      if (basicType === 'fixed') {
        var fixedCents = Math.min(basicValue * 100, eligibleSubtotal);
        result.totalDiscount = fixedCents;
        var distributed = 0;
        var lastEligibleIdx = -1;
        cart.items.forEach(function (item, idx) {
          if (isItemEligible(item, entitled)) lastEligibleIdx = idx;
        });
        result.itemDiscounts = cart.items.map(function (item, idx) {
          if (!isItemEligible(item, entitled)) return 0;
          var origLine = getOriginalLinePrice(item);
          var share;
          if (idx === lastEligibleIdx) {
            share = fixedCents - distributed;
          } else {
            share = Math.round(fixedCents * (origLine / eligibleSubtotal));
          }
          distributed += share;
          return Math.max(share, 0);
        });
      } else {
        // Percentage
        var pct = basicValue / 100;
        var pctTotal = 0;
        result.itemDiscounts = cart.items.map(function (item) {
          if (!isItemEligible(item, entitled)) return 0;
          var origLine = getOriginalLinePrice(item);
          var disc = Math.round(origLine * pct);
          pctTotal += disc;
          return disc;
        });
        result.totalDiscount = pctTotal;
      }
    }

    return result;
  }

  // Check if Shopify's cart already has a specific discount code applied
  function isDiscountAlreadyInCart(cart, code) {
    if (!cart || !code) return false;
    var normalized = code.trim().toUpperCase();

    // Check cart_level_discount_applications
    if (Array.isArray(cart.cart_level_discount_applications)) {
      var found = cart.cart_level_discount_applications.some(function (app) {
        return String(app.title || '').trim().toUpperCase() === normalized;
      });
      if (found) return true;
    }

    // Check item-level discounts
    if (Array.isArray(cart.items)) {
      return cart.items.some(function (item) {
        return Array.isArray(item.discounts) && item.discounts.some(function (d) {
          return String(d.title || '').trim().toUpperCase() === normalized;
        });
      });
    }

    return false;
  }

  function patchCartWithCoupon(cart) {
    if (!cart) return cart;

    var coupon = getAppliedCouponConfig();
    var code = coupon ? (optimisticAppliedCode || workerAppliedCodes[0] || coupon.code) : '';

    // If Shopify already has THIS EXACT discount applied AND we have it as active, don't patch.
    // When no coupon is active (code is empty), always restore original prices even if
    // Shopify still has a stale discount in session (e.g. after remove but iframe didn't clear).
    var shopifyAlreadyHasIt = coupon && code && isDiscountAlreadyInCart(cart, code);

    // Create a shallow copy
    var patched = {};
    for (var k in cart) {
      if (cart.hasOwnProperty(k)) patched[k] = cart[k];
    }
    patched._vayronPatched = true;

    // Always restore items to original prices first (removes any Shopify or previous coupon discount)
    var originalSubtotal = 0;
    if (Array.isArray(cart.items)) {
      patched.items = cart.items.map(function (item) {
        var restored = {};
        for (var ik in item) {
          if (item.hasOwnProperty(ik)) restored[ik] = item[ik];
        }
        var origLine = getOriginalLinePrice(item);
        var qty = item.quantity || 1;
        restored.original_line_price = origLine;
        restored.final_line_price = origLine;
        restored.line_price = origLine;
        restored.final_price = qty > 0 ? Math.round(origLine / qty) : 0;
        restored.discounts = [];
        originalSubtotal += origLine;
        return restored;
      });
    } else {
      originalSubtotal = Number(cart.items_subtotal_price || cart.original_total_price || cart.total_price || 0);
    }

    // Now apply the active coupon's discount on top of original prices
    if (coupon && code && !shopifyAlreadyHasIt) {
      var discountResult = calculateItemDiscounts(coupon, { items: patched.items });
      var totalDiscount = discountResult.totalDiscount;

      if (totalDiscount > 0) {
        patched.total_discount = totalDiscount;
        patched.total_price = Math.max(originalSubtotal - totalDiscount, 0);
        patched.cart_level_discount_applications = [
          { type: 'discount_code', title: code, total_allocated_amount: totalDiscount }
        ];

        // Apply per-item discounts
        patched.items = patched.items.map(function (item, idx) {
          var itemDiscount = discountResult.itemDiscounts[idx] || 0;
          if (itemDiscount <= 0) return item;

          var patchedItem = {};
          for (var ik in item) {
            if (item.hasOwnProperty(ik)) patchedItem[ik] = item[ik];
          }
          var origLine = item.original_line_price;
          var newLinePrice = Math.max(origLine - itemDiscount, 0);
          var qty = item.quantity || 1;

          patchedItem.final_line_price = newLinePrice;
          patchedItem.line_price = newLinePrice;
          patchedItem.final_price = qty > 0 ? Math.round(newLinePrice / qty) : 0;
          patchedItem.discounts = [{ title: code, amount: itemDiscount }];
          return patchedItem;
        });
      } else {
        // Coupon applies but discount is 0 (e.g. BXGY with no eligible items)
        patched.total_discount = 0;
        patched.total_price = originalSubtotal;
        patched.cart_level_discount_applications = [];
      }
    } else if (shopifyAlreadyHasIt) {
      // Shopify already has it — keep Shopify's data as-is (don't restore originals)
      patched.items = cart.items;
      patched.total_discount = Number(cart.total_discount || 0);
      patched.total_price = Number(cart.total_price || 0);
      patched.cart_level_discount_applications = cart.cart_level_discount_applications || [];
    } else {
      // No coupon — show original prices (already restored above)
      patched.total_discount = 0;
      patched.total_price = originalSubtotal;
      patched.cart_level_discount_applications = [];
    }

    // Keep items_subtotal_price in sync so milestone and other UI reads correct value
    patched.items_subtotal_price = originalSubtotal;

    return patched;
  }

  // Full re-render: use ONLY for apply/remove coupon actions
  function syncCartWithCoupon() {
    if (!state.cart) return;
    var patched = patchCartWithCoupon(state.cart);
    syncMiniCart(patched).catch(function () {});
  }

  // ---------------------------------------------------------------------------
  // Checkout interception — apply discount to Shopify only at checkout time
  // ---------------------------------------------------------------------------
  // Returns true if Shopify's cart has a code-based discount that we didn't apply
  // Get ALL discount code titles currently in Shopify's cart session
  function getShopifyCartCodes() {
    if (!state.cart) return [];
    var apps = state.cart.cart_level_discount_applications;
    if (!Array.isArray(apps)) return [];
    var codes = [];
    apps.forEach(function (app) {
      if (app && app.type === 'discount_code') {
        var title = String(app.title || '').trim().toUpperCase();
        if (title) codes.push(title);
      }
    });
    return codes;
  }

  var checkoutIntercepted = false;

  function interceptCheckout() {
    if (checkoutIntercepted) return;
    checkoutIntercepted = true;

    // Get the user's intended coupon code — check all sources, not just Worker
    function getIntendedCode() {
      // 1. optimistic (user just clicked Apply, Worker hasn't responded yet)
      if (optimisticAppliedCode) return optimisticAppliedCode;
      // 2. Worker-backed (source of truth after apply completes)
      if (workerAppliedCodes.length > 0) return workerAppliedCodes[0];
      // 3. sessionStorage (survives same-tab navigation)
      var session = getSessionAppliedCodes();
      if (session.length > 0) {
        var code = String(session[0]).trim().toUpperCase();
        if (code && !isCodeSuppressed(code)) return code;
      }
      return '';
    }

    // Determine checkout action based on coupon state vs Shopify state
    function decideCheckoutAction() {
      var intended = getIntendedCode();
      var shopifyCodes = getShopifyCartCodes();
      var shopifyHasCode = shopifyCodes.length > 0;

      if (intended) {
        // User has an intended coupon
        var shopifyAlreadyHasIt = shopifyCodes.indexOf(intended) !== -1;
        if (shopifyAlreadyHasIt) {
          // Shopify already has the user's coupon — DON'T TOUCH IT, let checkout proceed
          return { action: 'passthrough' };
        }
        // Shopify doesn't have it — apply via /discount/CODE redirect
        return { action: 'apply', code: intended };
      }

      // No intended coupon
      if (shopifyHasCode) {
        // Shopify has a code discount the user didn't apply — stale, clear it
        return { action: 'clear' };
      }

      // No coupon anywhere — let checkout proceed normally
      return { action: 'passthrough' };
    }

    function isShopfloLoaded() {
      return !!(window.shopflo || window.SHOPFLO || window.ShopFlo || document.querySelector('script[src*="shopflo"]'));
    }

    function injectDiscountIntoTarget(target, code) {
      if (!target || !code) return;
      try {
        if (target.tagName === 'A' && target.href) {
          var url = new URL(target.href, window.location.origin);
          url.searchParams.set('discount', code);
          target.href = url.toString();
        } else if (target.tagName === 'BUTTON') {
          var form = target.closest('form');
          if (form) {
            var hidden = form.querySelector('input[name="discount"]');
            if (!hidden) {
              hidden = document.createElement('input');
              hidden.type = 'hidden';
              hidden.name = 'discount';
              form.appendChild(hidden);
            }
            hidden.value = code;
          }
        }
      } catch (e) {}
    }

    function clearDiscountCookie_() {
      try {
        var names = ['discount_code', 'coupon_applied', 'hscodediscount'];
        var paths = ['/', '/cart', '/checkout'];
        var domains = [location.hostname, '.' + location.hostname];
        names.forEach(function(name) {
          paths.forEach(function(p) {
            domains.forEach(function(d) {
              document.cookie = name + '=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=' + p + '; domain=' + d;
            });
            document.cookie = name + '=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=' + p;
          });
        });
      } catch (e) {}
    }

    function setShopfloCouponCookies_(code) {
      try {
        // Set all three cookies Shopflo reads, so whichever it checks first finds the user's code
        var d = new Date();
        d.setDate(d.getDate() + 1);
        var expires = '; expires=' + d.toUTCString();
        document.cookie = 'discount_code=' + encodeURIComponent(code) + expires + '; path=/';
        document.cookie = 'coupon_applied=' + encodeURIComponent(code) + expires + '; path=/';
        document.cookie = 'hscodediscount=' + encodeURIComponent(code) + expires + '; path=/';
      } catch (e) {}
    }

    var shopfloCheckoutReady = false;

    function handleCheckout(event) {
      // If Shopflo is loaded, use the same decision logic as the native flow but execute it
      // via hidden iframes (instead of a full redirect) so Shopflo can still intercept the checkout click.
      if (isShopfloLoaded()) {
        // Re-trigger after we've already synced the session — let Shopflo handle this click
        if (shopfloCheckoutReady) {
          shopfloCheckoutReady = false;
          return;
        }

        var decision = decideCheckoutAction();
        if (decision.action === 'passthrough') {
          // Nothing to change — Shopflo can take over naturally
          return;
        }

        var btn = event.target.closest('a[name="checkout"], button[name="checkout"], .js-cart-btn-checkout, a[href^="/checkout"]');
        event.preventDefault();
        event.stopPropagation();

        var loadViaIframe = function(url, cb) {
          var ifr = document.createElement('iframe');
          ifr.style.display = 'none';
          var done = false;
          var finish = function() {
            if (done) return;
            done = true;
            try { ifr.remove(); } catch (e) {}
            cb();
          };
          ifr.onload = function() { setTimeout(finish, 150); };
          ifr.onerror = finish;
          ifr.src = url;
          document.body.appendChild(ifr);
          setTimeout(finish, 2500);
        };

        var triggerClick = function() {
          shopfloCheckoutReady = true;
          if (btn && typeof btn.click === 'function') {
            btn.click();
          } else if (btn && btn.form && typeof btn.form.submit === 'function') {
            btn.form.submit();
          }
        };

        if (decision.action === 'clear') {
          // Stale Shopify code that user didn't apply — clear it
          clearDiscountCookieFallback();
          loadViaIframe('/discount/%20?redirect=%2Fcart', triggerClick);
        } else if (decision.action === 'apply' && decision.code) {
          // User has a code Shopify doesn't have yet — apply it
          injectDiscountIntoTarget(btn, decision.code);
          loadViaIframe('/discount/' + encodeURIComponent(decision.code) + '?redirect=%2Fcart', triggerClick);
        } else {
          triggerClick();
        }
        return;
      }

      var decision = decideCheckoutAction();

      if (decision.action === 'passthrough') {
        // Let native checkout proceed — don't touch anything
        return;
      }

      event.preventDefault();
      event.stopPropagation();

      if (decision.action === 'apply') {
        // Redirect to /discount/CODE?redirect=/checkout — sets code and goes to checkout
        window.location.href = '/discount/' + encodeURIComponent(decision.code) + '?redirect=%2Fcheckout';
        return;
      }

      if (decision.action === 'clear') {
        // Clear stale discount then go to checkout
        clearDiscountCookieFallback();
        var done = false;
        var iframe = document.createElement('iframe');
        iframe.style.display = 'none';
        var proceed = function () {
          if (done) return;
          done = true;
          try { iframe.remove(); } catch (e) {}
          window.location.href = '/checkout';
        };
        iframe.onload = function () { setTimeout(proceed, 100); };
        iframe.onerror = proceed;
        iframe.src = '/discount/%20?redirect=/cart';
        document.body.appendChild(iframe);
        setTimeout(proceed, 2000);
      }
    }

    document.addEventListener('click', function (event) {
      var btn = event.target.closest('a[name="checkout"], button[name="checkout"], .js-cart-btn-checkout, a[href="/checkout"]');
      if (!btn) return;
      handleCheckout(event);
    }, true);

    document.addEventListener('submit', function (event) {
      var form = event.target;
      if (!form) return;
      var action = form.getAttribute('action') || '';
      var hasCheckoutBtn = form.querySelector('[name="checkout"]');
      if (action.indexOf('/checkout') === -1 && !hasCheckoutBtn) return;
      handleCheckout(event);
    }, true);

    // Listen for Shopflo coupon/exit events — if user removes coupon in Shopflo modal, sync cart
    window.addEventListener('message', function (event) {
      var data = event.data;
      if (!data || !data.type) return;
      var type = String(data.type);
      var COUPON_EVENTS = ['FLO_COUPONS_UPDATED', 'FLO_EXIT_CHECKOUT', 'FLO_CART_UPDATE', 'CLOSE_CART_DATA'];
      if (COUPON_EVENTS.indexOf(type) === -1) return;
      syncFromShopflo_(data);
    }, false);
  }

  function syncFromShopflo_(messageData) {
    // Try to extract coupon list from the Shopflo message payload
    var shopfloCodes = [];
    var shopfloProvidedList = false; // did Shopflo actually send a coupons array?
    try {
      var payload = messageData && messageData.payload;
      if (payload) {
        var coupons = payload.coupons || payload.discount_codes || payload.discounts;
        if (Array.isArray(coupons)) {
          shopfloProvidedList = true;
          coupons.forEach(function (c) {
            var code = '';
            if (typeof c === 'string') code = c;
            else if (c && (c.code || c.discount_code || c.title)) code = c.code || c.discount_code || c.title;
            if (code) shopfloCodes.push(String(code).trim().toUpperCase());
          });
        }
      }
    } catch (e) {}

    var localCode = optimisticAppliedCode || (workerAppliedCodes.length > 0 ? workerAppliedCodes[0] : '');
    var localCodeUpper = localCode ? String(localCode).trim().toUpperCase() : '';

    // Decide what changed between Shopflo state and local state
    var shopfloHasNewCode = shopfloProvidedList && shopfloCodes.length > 0 && shopfloCodes[0] !== localCodeUpper;
    var shopfloHasNoCode = (shopfloProvidedList && shopfloCodes.length === 0) || (!shopfloProvidedList && messageData.type === 'FLO_EXIT_CHECKOUT');
    var localHasCode = !!localCodeUpper;

    if (shopfloHasNewCode) {
      // Shopflo applied a different code than cart-coupons has — sync local to match Shopflo
      var newCode = shopfloCodes[0];

      // Suppress + remove old local code if any
      if (localHasCode) {
        addSuppressedCode(localCodeUpper);
        removeUserAppliedCode(localCodeUpper);
        removeSessionAppliedCode(localCodeUpper);
        workerAppliedCodes = workerAppliedCodes.filter(function (c) { return c !== localCodeUpper; });
        try { workerRemove(localCodeUpper).catch(function () {}); } catch (e) {}
      }

      // Add new code to local state
      removeSuppressedCode(newCode);
      addUserAppliedCode(newCode);
      addSessionAppliedCode(newCode);
      optimisticAppliedCode = newCode;
      try { workerApply(newCode).catch(function () {}); } catch (e) {}

      requestCart(true).then(function (freshCart) {
        if (freshCart) { state.cart = freshCart; render(); syncCartWithCoupon(); }
      }).catch(function () {});
      return;
    }

    if (shopfloHasNoCode && localHasCode) {
      // Shopflo removed the coupon — sync local to match
      addSuppressedCode(localCodeUpper);
      removeUserAppliedCode(localCodeUpper);
      removeSessionAppliedCode(localCodeUpper);
      if (optimisticAppliedCode === localCodeUpper) optimisticAppliedCode = null;
      workerAppliedCodes = workerAppliedCodes.filter(function (c) { return c !== localCodeUpper; });
      try { workerRemove(localCodeUpper).catch(function () {}); } catch (e) {}

      clearDiscountCookieFallback();
      try {
        var clearIframe = document.createElement('iframe');
        clearIframe.style.display = 'none';
        clearIframe.src = '/discount/%20?redirect=/cart';
        document.body.appendChild(clearIframe);
        setTimeout(function () { try { clearIframe.remove(); } catch (e) {} }, 2000);
      } catch (e) {}

      requestCart(true).then(function (freshCart) {
        if (freshCart) {
          stripCodeDiscounts(freshCart);
          state.cart = freshCart;
          render();
          syncCartWithCoupon();
        }
      }).catch(function () {});
      return;
    }

    // No change detected (Shopflo has same code as local, or didn't provide list)
  }

  // ---------------------------------------------------------------------------
  // Manual code input
  // ---------------------------------------------------------------------------
  function showManualError(form, msg) {
    var errEl = form.querySelector('[data-cart-coupon-error]');
    if (!errEl) return;
    errEl.textContent = msg;
    errEl.hidden = false;
  }

  function clearManualError(form) {
    var errEl = form.querySelector('[data-cart-coupon-error]');
    if (!errEl) return;
    errEl.textContent = '';
    errEl.hidden = true;
  }

  // Validate a rule against the current cart; returns error string or null if valid
  function validateCouponRule(rule, code) {
    if (!rule) return 'Invalid coupon code. Please check and try again.';

    if (rule.status === 'EXPIRED') return 'This coupon has expired.';
    if (rule.startsAt && new Date(rule.startsAt) > new Date()) return 'This coupon is not active yet.';
    if (rule.endsAt && new Date(rule.endsAt) < new Date()) return 'This coupon has expired.';
    if (rule.usageLimit && rule.asyncUsageCount >= rule.usageLimit) return 'This coupon has reached its usage limit.';

    var cart = state.cart;
    var currency = getCurrency(cart);
    var subtotalCents = cart ? getOriginalSubtotal(cart) : 0;

    if (rule.minimumRequirement) {
      if (rule.minimumRequirement.type === 'subtotal') {
        var minCents = rule.minimumRequirement.amount * 100;
        if (subtotalCents < minCents) {
          return 'Minimum purchase of ' + formatMoney(minCents, currency) + ' required.';
        }
      }
      if (rule.minimumRequirement.type === 'quantity') {
        var totalQty = 0;
        if (cart && Array.isArray(cart.items)) {
          cart.items.forEach(function (item) { totalQty += (item.quantity || 0); });
        }
        if (totalQty < rule.minimumRequirement.quantity) {
          return 'Minimum ' + rule.minimumRequirement.quantity + ' item' + (rule.minimumRequirement.quantity === 1 ? '' : 's') + ' required.';
        }
      }
    }

    if (rule.kind === 'bxgy') {
      if (!meetsCustomerBuysRequirement(rule, cart)) {
        if (rule.buysAmount && rule.buysAmount > 0) {
          return 'Add ' + formatMoney(rule.buysAmount * 100, currency) + ' of eligible items to use this coupon.';
        }
        var reqQty = rule.buysQuantity || 1;
        return 'Add ' + reqQty + ' eligible item' + (reqQty === 1 ? '' : 's') + ' to use this coupon.';
      }
    }

    if (rule.kind === 'basic' && rule.entitled && !rule.entitled.allItems) {
      var hasEligible = false;
      if (cart && Array.isArray(cart.items)) {
        hasEligible = cart.items.some(function (item) {
          return isItemEligible(item, rule.entitled);
        });
      }
      if (!hasEligible) {
        var collNames = (rule.entitled.collectionNames || []).join(', ');
        return collNames
          ? 'This coupon only applies to: ' + collNames + '. Add eligible items.'
          : 'No eligible items in your cart for this coupon.';
      }
    }

    return null; // All checks passed
  }

  // Ensure a code has a config entry so getAppliedCouponConfig can find it
  function ensureCouponConfig(code, rule) {
    if (state.config.coupons.find(function (c) { return c.code === code; })) return;
    state.config.coupons.push({
      id: 'manual-' + code,
      code: code,
      offerTitle: rule.title || code,
      offerDescription: '',
      minimumCartValue: rule.minimumRequirement && rule.minimumRequirement.type === 'subtotal'
        ? rule.minimumRequirement.amount : 0,
      discountType: rule.discountType || 'percentage',
      discountValue: rule.discountValue || 0,
      discountKind: rule.kind || 'basic',
      tag: '',
      displaySurface: 'both',
      scopeType: rule.entitled && rule.entitled.allItems ? 'AllDiscountItems' : 'scoped',
      requirementValue: rule.buysQuantity || 0,
      rewardQuantity: rule.rewardQuantity || 1
    });
  }

  function applyManualCode(form) {
    var input = form.querySelector('[data-cart-coupon-input]');
    if (!input) return;

    var code = String(input.value || '').trim().toUpperCase();
    clearManualError(form);

    if (!code) {
      input.focus();
      return;
    }

    var submitBtn = form.querySelector('[data-cart-coupon-submit]');
    var origLabel = submitBtn ? submitBtn.textContent : 'Apply';
    if (submitBtn) {
      submitBtn.disabled = true;
      submitBtn.textContent = '...';
    }

    function resetBtn() {
      if (submitBtn) {
        submitBtn.disabled = false;
        submitBtn.textContent = origLabel;
      }
    }

    function processRule(rule) {
      if (!rule) {
        showManualError(form, 'Invalid coupon code. Please check and try again.');
        return;
      }
      couponRules[code] = rule;
      var error = validateCouponRule(rule, code);
      if (error) {
        showManualError(form, error);
        return;
      }
      input.value = '';
      ensureCouponConfig(code, rule);
      applyCouponCode(code);
    }

    // Fetch coupon rules from Worker — use workerGetCoupons pattern that works on init
    var fetchUrl = WORKER_URL + '/coupons?codes=' + encodeURIComponent(code);

    // Try fetch first, then XHR fallback, then local-only fallback
    var fetchDone = false;

    function handleWorkerResult(data) {
      if (fetchDone) return;
      fetchDone = true;
      resetBtn();
      var rules = (data && data.rules) || {};
      processRule(rules[code] || null);
    }

    function handleWorkerError() {
      if (fetchDone) return;
      fetchDone = true;
      resetBtn();
      var localRule = getCouponRule(code);
      if (localRule) {
        processRule(localRule);
      } else {
        showManualError(form, 'Invalid coupon code. Please check and try again.');
      }
    }

    // Timeout: if neither fetch nor XHR responds in 8s, fall back to local
    var fallbackTimer = setTimeout(handleWorkerError, 8000);

    try {
      fetch(fetchUrl)
        .then(function (r) {
          if (!r.ok) throw new Error('HTTP ' + r.status);
          return r.json();
        })
        .then(function (data) {
          clearTimeout(fallbackTimer);
          handleWorkerResult(data);
        })
        .catch(function () {
          // fetch failed — try XHR as fallback
          try {
            var xhr = new XMLHttpRequest();
            xhr.open('GET', fetchUrl, true);
            xhr.onload = function () {
              clearTimeout(fallbackTimer);
              if (xhr.status === 200) {
                try { handleWorkerResult(JSON.parse(xhr.responseText)); }
                catch (e) { handleWorkerError(); }
              } else {
                handleWorkerError();
              }
            };
            xhr.onerror = function () { clearTimeout(fallbackTimer); handleWorkerError(); };
            xhr.send();
          } catch (e) {
            clearTimeout(fallbackTimer);
            handleWorkerError();
          }
        });
    } catch (e) {
      clearTimeout(fallbackTimer);
      handleWorkerError();
    }
  }

  // ---------------------------------------------------------------------------
  // Success popup & confetti
  // ---------------------------------------------------------------------------
  function playSuccessAnimation() {
    var popup = getSuccessPopup();
    if (!popup) return;
    var lottie = popup.querySelector('lottie-player');
    if (lottie && typeof lottie.stop === 'function') {
      lottie.stop();
      lottie.seek(0);
      lottie.setSpeed(1);
      lottie.play();
    }
  }

  function playSavingsConfetti() {
    if (!isMiniCartOpen()) return;
    // Delegate to theme.js's milestone confetti — it handles lazy lottie-player loading,
    // waits for the custom element to upgrade, and toggles the is-active class properly.
    try {
      if (window.theme && window.theme.miniCart && typeof window.theme.miniCart.playMilestoneConfetti === 'function') {
        window.theme.miniCart.playMilestoneConfetti();
      }
    } catch (e) { /* best-effort */ }
  }

  function buildAnimatedSavingsMarkup(amount, currency, isFinal) {
    var text = formatSavingsAmount(amount, currency);
    if (isFinal || text.length < 2) return escapeHtml(text);

    var last = text.slice(-1);
    var rest = text.slice(0, -1);
    return escapeHtml(rest) + '<span class="cart-coupon-success__digit' + (isFinal ? '' : ' is-jitter') + '">' + escapeHtml(last) + '</span>';
  }

  function animateSavings(start, end) {
    var popup = getSuccessPopup();
    if (!popup) return;
    var valueEl = popup.querySelector('[data-cart-coupon-success-value]');
    if (!valueEl) return;

    var currency = getCurrency(state.cart);
    var duration = 1200;
    var startTime = null;

    if (state.savingsPopupFrame) cancelAnimationFrame(state.savingsPopupFrame);

    function step(timestamp) {
      if (!startTime) startTime = timestamp;
      var progress = Math.min((timestamp - startTime) / duration, 1);
      var eased = 1 - Math.pow(1 - progress, 3);
      var current = Math.round(start + (end - start) * eased);

      valueEl.innerHTML = buildAnimatedSavingsMarkup(current, currency, progress >= 1);

      if (progress < 1) {
        state.savingsPopupFrame = requestAnimationFrame(step);
      } else {
        state.savingsPopupFrame = null;
      }
    }

    state.savingsPopupFrame = requestAnimationFrame(step);
  }

  function showSavingsPopup(savingsCents, code) {
    if (!isMiniCartOpen()) return false;

    var popup = getSuccessPopup();
    if (!popup) return false;

    var titleEl = popup.querySelector('[data-cart-coupon-success-title]');
    var subtextEl = popup.querySelector('[data-cart-coupon-success-subtext]');
    var currency = getCurrency(state.cart);

    if (titleEl) titleEl.textContent = code + ' applied!';
    if (subtextEl) subtextEl.textContent = 'You saved ' + formatMoney(savingsCents, currency);

    popup.hidden = false;
    popup.setAttribute('aria-hidden', 'false');
    document.body.classList.add('cart-coupon-success-open');

    requestAnimationFrame(function () {
      popup.classList.add('is-open');
    });

    playSuccessAnimation();
    playSavingsConfetti();
    animateSavings(0, savingsCents);

    clearTimeout(state.savingsPopupAutoCloseTimer);
    state.savingsPopupAutoCloseTimer = setTimeout(function () {
      closeSavingsPopup();
    }, POPUP_DURATION);

    return true;
  }

  function closeSavingsPopup(force) {
    var popup = getSuccessPopup();
    if (!popup) return;

    clearTimeout(state.savingsPopupAutoCloseTimer);
    state.savingsPopupAutoCloseTimer = null;

    if (state.savingsPopupFrame) {
      cancelAnimationFrame(state.savingsPopupFrame);
      state.savingsPopupFrame = null;
    }

    document.body.classList.remove('cart-coupon-success-open');
    popup.classList.remove('is-open');

    if (force) {
      popup.hidden = true;
      popup.setAttribute('aria-hidden', 'true');
    } else {
      setTimeout(function () {
        popup.hidden = true;
        popup.setAttribute('aria-hidden', 'true');
      }, 260);
    }
  }

  function maybeShowPendingSavingsPopup(collections) {
    var pending = getPendingSavingsPopup();
    if (!pending || !collections || !state.cart) return;

    var applied = getAppliedPreviewState(collections);
    if (!applied || !applied.code || String(applied.code).trim().toUpperCase() !== pending.code) return;

    if (!isCouponCodeInCart(state.cart, applied.code)) {
      clearPendingSavingsPopup();
      return;
    }

    if (applied.savingsCents > 0 && showSavingsPopup(applied.savingsCents, applied.code)) {
      clearPendingSavingsPopup();
    }
  }

  // Determine whether a coupon is currently "unlocked" (would actually apply savings) for a given cart
  function getCouponUnlockState(coupon, cart) {
    if (!coupon || !cart) return { unlocked: false, savings: 0, freeShipping: false };

    // Minimum cart value gate
    var minCents = (Number(coupon.minimumCartValue) || 0) * 100;
    var subtotal = getOriginalSubtotal(cart);
    if (minCents > 0 && subtotal < minCents) {
      return { unlocked: false, savings: 0, freeShipping: false };
    }

    // BXGY customerBuys requirement gate
    var rule = getCouponRule(coupon.code);
    if (rule && rule.kind === 'bxgy' && !meetsCustomerBuysRequirement(rule, cart)) {
      return { unlocked: false, savings: 0, freeShipping: false };
    }

    var discount = calculateItemDiscounts(coupon, cart);
    var unlocked = (discount && (discount.totalDiscount > 0 || discount.freeShipping)) || false;
    return {
      unlocked: unlocked,
      savings: (discount && discount.totalDiscount) || 0,
      freeShipping: (discount && discount.freeShipping) || false
    };
  }

  // Show the milestone-unlocked variant of the popup (different copy from "applied")
  function showMilestonePopup(savingsCents, code) {
    if (!showSavingsPopup(savingsCents, code)) return;
    var popup = getSuccessPopup();
    if (!popup) return;
    var titleEl = popup.querySelector('[data-cart-coupon-success-title]');
    var subtextEl = popup.querySelector('[data-cart-coupon-success-subtext]');
    if (titleEl) titleEl.textContent = code + ' unlocked!';
    if (subtextEl) {
      subtextEl.textContent = savingsCents > 0
        ? 'Save ' + formatMoney(savingsCents, getCurrency(state.cart))
        : 'Offer ready for you';
    }
  }

  // Fire popup + confetti ONLY when a coupon transitions from locked → unlocked.
  // Does NOT fire when an already-unlocked coupon's savings merely increase.
  function maybeShowTransitionSavingsPopup(previousCart, nextCart) {
    // Popup + confetti only on explicit coupon apply — not on milestone unlock
    return;
  }

  // sessionStorage: pending savings popup (for page reload scenarios)
  function setPendingSavingsPopup(code) {
    try {
      window.sessionStorage.setItem('vayronCartCouponPendingPopup', JSON.stringify({
        code: String(code).trim().toUpperCase(),
        timestamp: Date.now()
      }));
    } catch (e) { /* ignore */ }
  }

  function getPendingSavingsPopup() {
    try {
      var data = JSON.parse(window.sessionStorage.getItem('vayronCartCouponPendingPopup'));
      if (data && data.code && (Date.now() - data.timestamp) < 300000) {
        return data;
      }
      clearPendingSavingsPopup();
    } catch (e) { /* ignore */ }
    return null;
  }

  function clearPendingSavingsPopup() {
    try {
      window.sessionStorage.removeItem('vayronCartCouponPendingPopup');
    } catch (e) { /* ignore */ }
  }

  // ---------------------------------------------------------------------------
  // Drawer open/close
  // ---------------------------------------------------------------------------
  function openDrawer() {
    var sheet = getSheet();
    if (!sheet) return;

    sheet.classList.remove('is-closing');
    sheet.hidden = false;

    requestAnimationFrame(function () {
      sheet.classList.add('is-open');
    });

    document.body.classList.add('cart-coupon-sheet-open');
    sheet.setAttribute('aria-hidden', 'false');

    if (!state.cart) {
      refreshCoupons(true);
    }
  }

  function closeDrawer(force) {
    var sheet = getSheet();
    if (!sheet) return;

    if (force) {
      sheet.classList.remove('is-open');
      sheet.classList.remove('is-closing');
      sheet.hidden = true;
      document.body.classList.remove('cart-coupon-sheet-open');
      sheet.setAttribute('aria-hidden', 'true');
      return;
    }

    sheet.classList.remove('is-open');
    sheet.classList.add('is-closing');
    document.body.classList.remove('cart-coupon-sheet-open');

    setTimeout(function () {
      sheet.classList.remove('is-closing');
      sheet.hidden = true;
      sheet.setAttribute('aria-hidden', 'true');
    }, 320);
  }

  function isCouponDrawerOpen() {
    var sheet = getSheet();
    return sheet && sheet.classList.contains('is-open');
  }

  // ---------------------------------------------------------------------------
  // Event binding
  // ---------------------------------------------------------------------------
  function bindEvents() {
    if (state.initialized) return;

    // Click handler
    document.addEventListener('click', function (event) {
      // Remove coupon — checked FIRST so it isn't intercepted by other handlers on mobile
      if (event.target.closest('[data-cart-coupon-clear]')) {
        event.preventDefault();
        event.stopPropagation();
        clearAppliedCode();
        return;
      }

      // Apply button (button with data-cart-coupon-apply)
      var applyBtn = event.target.closest('[data-cart-coupon-apply]');
      if (applyBtn) {
        event.preventDefault();
        var code = applyBtn.getAttribute('data-cart-coupon-apply');
        if (code) applyCouponCode(code);
        return;
      }

      // Open drawer
      if (event.target.closest('[data-cart-coupon-open]')) {
        event.preventDefault();
        openDrawer();
        return;
      }

      // Close drawer
      if (event.target.closest('[data-cart-coupon-close]')) {
        event.preventDefault();
        closeDrawer();
        return;
      }

      // Close success popup
      if (event.target.closest('[data-cart-coupon-success-close]')) {
        event.preventDefault();
        closeSavingsPopup();
        return;
      }

      // Tab switch
      var tabButton = event.target.closest('[data-cart-coupon-tab]');
      if (tabButton) {
        event.preventDefault();
        state.activeTab = tabButton.getAttribute('data-cart-coupon-tab') || 'all';
        render();
        return;
      }

      // Cart quantity buttons — schedule refresh
      if (event.target.closest('.js-qty__minus')) {
        state.lastCartAction = 'decrease';
        scheduleRefresh(450);
        return;
      }
      if (event.target.closest('.js-qty__plus')) {
        state.lastCartAction = 'increase';
        scheduleRefresh(450);
        return;
      }
      if (event.target.closest('.js-remove-mini-cart')) {
        state.lastCartAction = 'remove';
        scheduleRefresh();
        return;
      }
      if (event.target.closest('.update-cart, .add-gift__btn')) {
        state.lastCartAction = 'update';
        scheduleRefresh();
      }
    });

    // Change handler
    document.addEventListener('change', function (event) {
      if (event.target.closest('.cart__quantity, .change-minicart')) {
        state.lastCartAction = 'manual-quantity';
        scheduleRefresh(450);
      }
    });

    // Clear error on input
    document.addEventListener('input', function (event) {
      if (event.target.matches('[data-cart-coupon-input]')) {
        var form = event.target.closest('[data-cart-coupon-form]');
        if (form) clearManualError(form);
      }
    });

    // Submit handler
    document.addEventListener('submit', function (event) {
      var form = event.target.closest('[data-cart-coupon-form]');
      if (form) {
        event.preventDefault();
        applyManualCode(form);
      }
    });

    // Keyboard
    document.addEventListener('keydown', function (event) {
      if (event.key === 'Escape') {
        closeDrawer();
        closeSavingsPopup();
      }
    });

    // Shopify theme editor events
    document.addEventListener('shopify:section:load', init);
    document.addEventListener('shopify:section:reorder', init);
    document.addEventListener('shopify:block:select', init);

    // External cart updates
    document.addEventListener('theme:cart:updated', function (event) {
      if (event.detail && event.detail.cart) {
        handleExternalCartUpdate(event.detail.cart);
      }
    });

    // Mini-cart close
    document.addEventListener('theme:mini-cart:toggled', function (event) {
      if (event.detail && event.detail.active === false) {
        closeDrawer(true);
        closeSavingsPopup(true);
      }
    });

    state.initialized = true;
  }

  // ---------------------------------------------------------------------------
  // Init
  // ---------------------------------------------------------------------------
  function init() {
    state.config = parseConfig();
    state.activeTab = 'all';

    if (!state.config) {
      clearCoupons();
      return;
    }

    // Load coupon rules from Worker (Shopify source of truth — entitlements, scopes, BXGY config)
    workerGetCoupons().then(function (result) {
      if (result && result.rules) {
        couponRules = result.rules;
        render();
        if (getAppliedCouponConfig()) syncCartWithCoupon();
      }
    }).catch(function () {});

    // Restore session-applied codes — survives same-tab navigation (e.g. checkout → back)
    // but NOT fresh browser sessions. This prevents auto-apply on new visits.
    var sessionCodes = getSessionAppliedCodes();
    if (sessionCodes.length > 0) {
      sessionCodes.forEach(function (code) {
        var normalized = String(code).trim().toUpperCase();
        if (normalized && !isCodeSuppressed(normalized)) {
          addUserAppliedCode(normalized);
        }
      });
      // Re-render with restored state once cart is available (max 5s)
      var renderAttempts = 0;
      var waitForCartAndRender = function () {
        if (state.cart) {
          render();
          syncCartWithCoupon();
        } else if (renderAttempts++ < 50) {
          setTimeout(waitForCartAndRender, 100);
        }
      };
      setTimeout(waitForCartAndRender, 200);
    } else {
      // No session-applied codes — clear any stale Shopify discount after cart loads (max 5s)
      var clearAttempts = 0;
      var waitForCartAndClear = function () {
        if (state.cart) {
          clearAutoAppliedDiscounts(state.cart);
        } else if (clearAttempts++ < 50) {
          setTimeout(waitForCartAndClear, 100);
        }
      };
      setTimeout(waitForCartAndClear, 200);
    }

    // Intercept checkout clicks to apply discount at checkout time
    interceptCheckout();

    var sheet = getSheet();
    if (sheet) {
      sheet.hidden = true;
      sheet.setAttribute('aria-hidden', 'true');
    }

    var popup = getSuccessPopup();
    if (popup) {
      popup.hidden = true;
      popup.setAttribute('aria-hidden', 'true');
    }

    bindEvents();

    // Load cart — try multiple paths to ensure we always get cart data.
    // Path 1: mini-cart module already has it (instant)
    // Path 2: listen for theme:cart:updated event (async, after theme.js fetches cart)
    // Path 3: direct fetch /cart.js after 1.5s (guaranteed fallback)
    // Path 4: safety-net fetch after 4s (absolute last resort)
    function loadInitialCart() {
      var loaded = false;

      function setCart(cart) {
        if (loaded) return;
        if (!cart || typeof cart.item_count === 'undefined') return;
        loaded = true;
        state.cart = cart;
        render();
      }

      // Path 1: mini-cart module may already have a cached cart
      var mod = getMiniCartModule();
      if (mod && typeof mod.getCurrentCart === 'function') {
        var cached = mod.getCurrentCart();
        if (cached && typeof cached.item_count !== 'undefined') {
          setCart(cached);
          return;
        }
      }

      // Path 2: listen for theme:cart:updated (theme.js fetches cart on page load)
      var onCartUpdated = function (event) {
        if (event.detail && event.detail.cart) {
          setCart(event.detail.cart);
        }
        if (loaded) {
          document.removeEventListener('theme:cart:updated', onCartUpdated);
        }
      };
      document.addEventListener('theme:cart:updated', onCartUpdated);

      // Path 3: direct fetch after 1.5s (bypass jQuery deferred interop issues)
      setTimeout(function () {
        if (loaded) return;
        fetch('/cart.js', { credentials: 'same-origin', headers: { Accept: 'application/json' } })
          .then(function (r) { return r.json(); })
          .then(function (cart) { setCart(cart); })
          .catch(function () {});
      }, 1500);

      // Path 4: absolute safety-net after 4s
      setTimeout(function () {
        if (loaded) return;
        fetch('/cart.js', { credentials: 'same-origin', headers: { Accept: 'application/json' } })
          .then(function (r) { return r.json(); })
          .then(function (cart) { setCart(cart); })
          .catch(function () {});
      }, 4000);
    }

    loadInitialCart();
  }

  // Start
  function safeInit() {
    try {
      init();
    } catch (e) {
      console.error('[cart-coupons] init error:', e);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', safeInit);
  } else {
    safeInit();
  }
})();
